import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, MousePointerClick, DollarSign, Bot, Star, LayoutTemplate, Layout, Mail, Target, Eye, Link as LinkIcon, X, CircleCheck, Check, ShieldCheck, Loader2, Trash2, ArrowRight, ArrowLeft, Rocket, Edit2, Monitor, MonitorSmartphone, Copy, Download, LogOut, Search, Play, Globe, MessageCircle, Zap, Shield, User, Users, Music, Bell, ExternalLink, ChevronDown, Plus, Compass, RefreshCw, Megaphone, Share2, Settings as SettingsIcon } from 'lucide-react';
import LandingPage from './components/LandingPage';
import FunnelVisualizer from './components/FunnelVisualizer';
import CompetitionSpyModal from './components/CompetitionSpyModal';
import OfferScoutModal from './components/OfferScoutModal';
import OfferScout from './pages/OfferScout';
import SettingsPage from './pages/Settings';
import AdStudioModal from './components/AdStudioModal';
import CampaignBuilder from './pages/CampaignBuilder';
import CampaignWizard from './components/CampaignWizard';
import AngleComparisonTable from './components/AngleComparisonTable';
import PaidTrafficManager from './components/PaidTrafficManager';

import { auth, db } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';

const TRAFFIC_TYPES = ['Email', 'Contextual', 'Display', 'Search', 'Incentive', 'Social Media', 'Native', 'Mobile', 'Brand Bid'];

const mockChartData = [
  { name: 'Mon', clicks: 400, conversions: 24, revenue: 120 },
  { name: 'Tue', clicks: 300, conversions: 18, revenue: 90 },
  { name: 'Wed', clicks: 550, conversions: 35, revenue: 175 },
  { name: 'Thu', clicks: 480, conversions: 40, revenue: 200 },
  { name: 'Fri', clicks: 600, conversions: 50, revenue: 250 },
  { name: 'Sat', clicks: 750, conversions: 65, revenue: 325 },
  { name: 'Sun', clicks: 800, conversions: 75, revenue: 375 },
];

// --- Components ---

const FunnelAnalyticsModal = ({ campaign, isOpen, onClose }) => {
  const [viewType, setViewType] = useState('map');
  
  if (!isOpen || !campaign) return null;

  const variants = campaign.pageVariants || [];
  const totalCampaignClicks = campaign.clicks || 0;
  const pageNames = variants.map(v => v?.name || '');

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-slate-900 border border-slate-700 rounded-[2.5rem] w-full max-w-4xl shadow-2xl overflow-hidden relative flex flex-col h-[80vh]">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-500"></div>
        
        <div className="p-8 pb-4 flex items-center justify-between border-b border-slate-800 shrink-0">
          <div className="flex items-center">
            <div className="p-2 bg-emerald-500/10 text-emerald-400 rounded-lg mr-4">
              <TrendingUp size={24} />
            </div>
            <div>
              <h3 className="text-2xl font-black text-white tracking-tight">Funnel Drop-off Intelligence</h3>
              <p className="text-xs text-slate-500 font-mono mt-1">/u/{campaign.username}/{campaign.slug}</p>
            </div>
          </div>
          
          <div className="flex bg-slate-950 p-1 rounded-xl border border-slate-800 mx-4">
            <button 
              onClick={() => setViewType('list')}
              className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${viewType === 'list' ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/20' : 'text-slate-500 hover:text-slate-300'}`}
            >
              List View
            </button>
            <button 
              onClick={() => setViewType('table')}
              className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${viewType === 'table' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20' : 'text-slate-500 hover:text-slate-300'}`}
            >
              Angle Table
            </button>
            <button 
              onClick={() => setViewType('map')}
              className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${viewType === 'map' ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/20' : 'text-slate-500 hover:text-slate-300'}`}
            >
              Visual Map
            </button>
          </div>


          <button onClick={onClose} className="p-2 text-slate-500 hover:text-white transition-colors">
            <X size={24} />
          </button>
        </div>
        
        <div className="flex-1 overflow-hidden bg-slate-950/50 flex flex-col">
          {viewType === 'list' ? (
            <div className="p-8 overflow-y-auto custom-scrollbar flex-1">
              <div className="space-y-6">
                {variants.length === 0 ? (
                  <div className="text-center text-slate-500 py-8 font-bold">No specific page variants tracked yet. Generate a funnel to see step-by-step metrics.</div>
                ) : (
                  variants.map((v, index) => {
                    const previousVariantClicks = index === 0 ? totalCampaignClicks : (variants[index-1].clicks || 0);
                    const currentClicks = v.clicks || 0;
                    
                    const baseDenominator = index === 0 ? totalCampaignClicks : previousVariantClicks;
                    const dropOffPercent = baseDenominator > 0 ? (((baseDenominator - currentClicks) / baseDenominator) * 100).toFixed(1) : 0;
                    const survivalPercent = baseDenominator > 0 ? ((currentClicks / baseDenominator) * 100).toFixed(1) : (currentClicks > 0 ? 100 : 0);

                    return (
                      <div key={v.id} className="relative">
                        {index !== variants.length - 1 && (
                          <div className="absolute top-1/2 left-8 w-1 h-32 bg-slate-800 -z-10 translate-y-1/2"></div>
                        )}
                        
                        <div className="bg-slate-900 border border-slate-700/50 p-6 rounded-3xl flex items-center shadow-lg relative group overflow-hidden">
                          <div className="absolute inset-0 bg-emerald-500/5 z-0" style={{ width: `${survivalPercent}%` }}></div>
                          
                          <div className="z-10 bg-slate-950 border border-slate-800 size-16 rounded-2xl flex items-center justify-center font-black text-2xl text-slate-400 mr-6 shadow-sm">
                            {index + 1}
                          </div>
                          
                          <div className="z-10 flex-1">
                            <div className="flex justify-between items-start mb-2">
                              <h4 className="font-black text-white text-lg uppercase tracking-wider">{v?.name || ''}</h4>
                              <div className="text-right">
                                <div className="text-2xl font-black text-white">{currentClicks}</div>
                                <div className="text-[10px] text-slate-500 uppercase font-black tracking-widest">Views</div>
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-4 text-xs font-bold">
                               <div className="text-emerald-400">
                                 {survivalPercent}% Survived from Previous Step
                               </div>
                               {index > 0 && (
                                 <div className="text-rose-400">
                                   {dropOffPercent}% Drop-off
                                 </div>
                               )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}

                <div className="relative pt-6">
                     <div className="bg-indigo-900/40 border border-indigo-500/30 p-6 rounded-3xl flex items-center shadow-lg relative overflow-hidden">
                          <div className="z-10 bg-indigo-950 border border-indigo-500/50 size-16 rounded-2xl flex items-center justify-center font-black text-2xl text-indigo-400 mr-6 shadow-sm">
                            <Target size={24} />
                          </div>
                          
                          <div className="z-10 flex-1">
                            <div className="flex justify-between items-start mb-2">
                              <h4 className="font-black text-white text-lg uppercase tracking-wider">Merchant Sales</h4>
                              <div className="text-right">
                                <div className="text-2xl font-black text-emerald-400">{campaign.conversions || 0}</div>
                                <div className="text-[10px] text-slate-400 uppercase font-black tracking-widest">Conversions</div>
                              </div>
                            </div>
                            <div className="text-xs text-indigo-300 font-bold">Total Conversion Rate: {(totalCampaignClicks > 0 ? ((campaign.conversions || 0) / totalCampaignClicks)*100 : 0).toFixed(2)}%</div>
                          </div>
                     </div>
                </div>
              </div>
            </div>
          ) : viewType === 'table' ? (
            <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
              <div className="mb-4">
                <h4 className="text-sm font-black text-white mb-1">📊 Angle Comparison Table</h4>
                <p className="text-[10px] text-slate-500">Live split-test metrics per angle. Two-proportion z-test for statistical confidence. SCALE/KILL requires ≥95% confidence.</p>
              </div>
              <AngleComparisonTable
                campaign={campaign}
                campaignId={campaign.id}
                breakEvenCpc={campaign.networkEpc || campaign.offerEpc || 0}
                onAngleUpdated={(idx, updatedAngle) => {
                  // Parent would need to refresh campaign data from Firestore
                  console.log('[ANGLE UPDATED]', idx, updatedAngle);
                }}
              />
            </div>
          ) : (
            <div className="flex-1 bg-slate-950 relative overflow-hidden">

              <FunnelVisualizer 
                trafficSource={campaign.trafficSource || 'General Traffic'} 
                pages={pageNames} 
                network={campaign.network || 'Merchant Offer'}
                campaignId={campaign.id}
                selectedStrategies={campaign.selectedStrategies || []}
              />
            </div>
          )}
        </div>
        
      </div>
    </div>
  );
};

const ProfileModal = ({ profile, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState(() => ({
    ...profile,
    customDomains: Array.isArray(profile?.customDomains) ? profile.customDomains : (profile?.customDomain ? [profile.customDomain] : []),
    geminiApiKey: localStorage.getItem('gemini_api_key') || '',
    openaiApiKey: localStorage.getItem('openai_api_key') || '',
    nanobananaApiKey: localStorage.getItem('nanobanana_api_key') || '',
  }));
  const [newDomainInput, setNewDomainInput] = useState('');
  const [domainError, setDomainError] = useState('');
  const [isTestingMailerXP, setIsTestingMailerXP] = useState(false);
  const [mailerXpData, setMailerXpData] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        ...profile,
        customDomains: Array.isArray(profile?.customDomains) ? profile.customDomains : (profile?.customDomain ? [profile.customDomain] : []),
        geminiApiKey: localStorage.getItem('gemini_api_key') || '',
        openaiApiKey: localStorage.getItem('openai_api_key') || '',
        nanobananaApiKey: localStorage.getItem('nanobanana_api_key') || '',
      });
      setNewDomainInput('');
      setDomainError('');
    }
  }, [isOpen, profile]);

  const handleAddDomain = () => {
    setDomainError('');
    if (!newDomainInput.trim()) return;
    const clean = newDomainInput.toLowerCase().trim().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    if (!clean || !clean.includes('.')) {
      setDomainError('Please enter a valid domain or subdomain (e.g. deals.mybrand.com)');
      return;
    }
    const currentList = Array.isArray(formData.customDomains) ? formData.customDomains : [];
    if (currentList.includes(clean)) {
      setDomainError('This domain is already in your list.');
      return;
    }
    setFormData(prev => ({
      ...prev,
      customDomains: [...currentList, clean]
    }));
    setNewDomainInput('');
  };

  const handleRemoveDomain = (domainToRemove) => {
    const currentList = Array.isArray(formData.customDomains) ? formData.customDomains : [];
    setFormData(prev => ({
      ...prev,
      customDomains: currentList.filter(d => d !== domainToRemove)
    }));
  };
  
  if (!isOpen) return null;

  const handleTestMailerXP = async () => {
    if (!formData?.mailerXpApi) return alert('Please enter a MailerXP API key first.');
    setIsTestingMailerXP(true);
    setMailerXpData(null);
    try {
      const [listsRes, segmentsRes] = await Promise.all([
        fetch('/api/mailerxp/lists', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ apiKey: formData.mailerXpApi })
        }),
        fetch('/api/mailerxp/segments', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ apiKey: formData.mailerXpApi })
        })
      ]);
      
      const lists = await listsRes.json();
      const segments = await segmentsRes.json();
      
      if (lists.error || segments.error) {
         alert('Error connecting to MailerXP: ' + (lists.error || segments.error));
      } else {
         // Assuming API might return array directly or `{ data: [...] }`
         const parsedLists = Array.isArray(lists) ? lists : (lists.data || lists.lists || []);
         const parsedSegments = Array.isArray(segments) ? segments : (segments.data || segments.segments || []);
         setMailerXpData({ lists: parsedLists, segments: parsedSegments });
      }
    } catch (err) {
      console.error(err);
      alert('Failed to connect to MailerXP.');
    }
    setIsTestingMailerXP(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-slate-900 border border-slate-700 rounded-[2.5rem] w-full max-w-lg shadow-2xl overflow-hidden relative max-h-[90vh] flex flex-col">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-rose-500"></div>
        
        <div className="p-8 text-left overflow-y-auto custom-scrollbar flex-1">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center">
              <div className="p-2 bg-indigo-500/10 text-indigo-400 rounded-lg mr-4">
                <Bot size={24} />
              </div>
              <h3 className="text-2xl font-black text-white tracking-tight">Global Ninja Profile</h3>
            </div>
            <button onClick={onClose} className="p-2 text-slate-500 hover:text-white transition-colors">
              <X size={24} />
            </button>
          </div>
          
          <div className="space-y-6">
            <div>
              <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center">
                <Target size={14} className="mr-2" /> Global Affiliate Username / ID
              </label>
              <input 
                type="text" 
                value={formData?.affiliateId || ''} 
                onChange={e => setFormData({ ...formData, affiliateId: e.target.value })}
                placeholder="e.g. ninja_master"
                className="w-full bg-slate-950 border border-slate-700 text-white px-5 py-4 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition font-mono text-lg shadow-inner"
              />
              <p className="text-[10px] text-slate-500 mt-3 italic leading-relaxed">This ID will be automatically injected into Step 1 of all new campaigns, eliminating redundant manual typing.</p>
            </div>

            <div className="pt-4 border-t border-slate-800">
              <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center">
                <Mail size={14} className="mr-2" /> MailerXP Autoresponder API
              </label>
              <div className="flex space-x-2">
                 <input 
                   type="text" 
                   value={formData?.mailerXpApi || ''} 
                   onChange={e => setFormData({ ...formData, mailerXpApi: e.target.value })}
                   placeholder="Paste MailerXP API Key..."
                   className="flex-1 bg-slate-950 border border-slate-700 text-white px-5 py-4 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition font-mono text-sm shadow-inner"
                 />
                 <button 
                   onClick={handleTestMailerXP}
                   disabled={isTestingMailerXP || !formData?.mailerXpApi}
                   className="px-6 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-indigo-400 font-bold rounded-2xl transition-colors flex items-center shadow-sm"
                 >
                   {isTestingMailerXP ? <Loader2 className="animate-spin size-5" /> : < Zap size={20} />}
                 </button>
              </div>
              <p className="text-[10px] text-slate-500 mt-3 italic leading-relaxed">Connect your MailerXP account to automatically sync leads from captured funnels.</p>
            </div>

            {/* Custom Domains & Subdomains Section */}
            <div className="pt-4 border-t border-slate-800 space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center">
                  <Globe size={14} className="mr-2 text-indigo-400" /> Custom Domains & Subdomains
                </label>
                <span className="text-[10px] text-slate-500 font-mono">
                  {(formData.customDomains || []).length} configured
                </span>
              </div>
              <p className="text-[11px] text-slate-400 leading-relaxed font-sans">
                Add your branded domain or subdomain (e.g. <code className="text-indigo-300 font-mono">deals.mybrand.com</code>). 
                When configured, you can simply pick which domain to run your campaign under with 1 click.
              </p>

              {/* Add Domain Input */}
              <div className="space-y-1.5">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newDomainInput}
                    onChange={e => { setNewDomainInput(e.target.value); setDomainError(''); }}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddDomain(); } }}
                    placeholder="e.g. deals.mybrand.com or mypromos.com"
                    className="flex-1 bg-slate-950 border border-slate-700 text-white px-4 py-3 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition font-mono text-xs shadow-inner"
                  />
                  <button
                    type="button"
                    onClick={handleAddDomain}
                    className="px-5 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-black text-xs uppercase tracking-wider rounded-2xl transition-all shadow-md shadow-indigo-600/20 active:scale-95 cursor-pointer shrink-0"
                  >
                    + Add Domain
                  </button>
                </div>
                {domainError && <p className="text-[10px] text-rose-400 font-medium">{domainError}</p>}
              </div>

              {/* List of Configured Domains */}
              <div className="space-y-2 pt-1">
                {(formData.customDomains || []).map((dom, i) => (
                  <div key={i} className="flex items-center justify-between p-3.5 rounded-2xl bg-slate-950 border border-slate-800 group hover:border-slate-700 transition-all">
                    <div className="flex items-center space-x-3 min-w-0">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0"></span>
                      <div className="min-w-0">
                        <div className="text-xs font-mono font-bold text-white truncate">{dom}</div>
                        <div className="text-[9px] text-slate-500 font-mono">CNAME &rarr; <span className="text-emerald-400 select-all font-bold">cname.xpgo.link</span></div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveDomain(dom)}
                      className="p-2 text-slate-500 hover:text-rose-400 rounded-xl hover:bg-rose-500/10 transition-colors cursor-pointer"
                      title="Remove domain"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                {(!formData.customDomains || formData.customDomains.length === 0) && (
                  <div className="p-4 rounded-2xl bg-slate-950/60 border border-slate-800/80 text-[10px] text-slate-500 italic">
                    No custom domains added yet. Your campaigns will default to <strong className="text-slate-400 not-italic font-mono">xpgo.link</strong> automatically without prompting you.
                  </div>
                )}
              </div>
            </div>

            <div className="pt-4 border-t border-slate-800">
               <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center">
                 <Shield size={14} className="mr-2 text-indigo-400" /> AI API Credentials
               </label>
               <div className="space-y-4">
                  <div>
                      <div className="flex justify-between items-center mb-1.5">
                          <label className="block text-[10px] font-black text-slate-500 uppercase">Gemini API Key</label>
                          <a href="https://aistudio.google.com/" target="_blank" rel="noopener noreferrer" className="text-[10.5px] text-indigo-400 hover:text-indigo-300 font-bold transition flex items-center">
                              Get Key <ExternalLink size={10} className="ml-1" />
                          </a>
                      </div>
                      <input 
                        type="password" 
                        value={formData?.geminiApiKey || ''} 
                        onChange={e => setFormData({ ...formData, geminiApiKey: e.target.value })}
                        placeholder="Paste Gemini API Key..."
                        className="w-full bg-slate-950 border border-slate-700 text-white px-4 py-3 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition font-mono text-xs shadow-inner"
                      />
                  </div>
                  <div>
                      <div className="flex justify-between items-center mb-1.5">
                          <label className="block text-[10px] font-black text-slate-500 uppercase">OpenAI API Key (Optional)</label>
                          <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-[10.5px] text-indigo-400 hover:text-indigo-300 font-bold transition flex items-center">
                              Get Key <ExternalLink size={10} className="ml-1" />
                          </a>
                      </div>
                      <input 
                        type="password" 
                        value={formData?.openaiApiKey || ''} 
                        onChange={e => setFormData({ ...formData, openaiApiKey: e.target.value })}
                        placeholder="Paste OpenAI API Key..."
                        className="w-full bg-slate-950 border border-slate-700 text-white px-4 py-3 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition font-mono text-xs shadow-inner"
                      />
                  </div>
                  <div>
                      <div className="flex justify-between items-center mb-1.5">
                          <label className="block text-[10px] font-black text-slate-500 uppercase">NanoBanana Pro Key (Google AI Studio)</label>
                          <a href="https://aistudio.google.com/" target="_blank" rel="noopener noreferrer" className="text-[10.5px] text-indigo-400 hover:text-indigo-300 font-bold transition flex items-center">
                              Get Key <ExternalLink size={10} className="ml-1" />
                          </a>
                      </div>
                      <input 
                        type="password" 
                        value={formData?.nanobananaApiKey || ''} 
                        onChange={e => setFormData({ ...formData, nanobananaApiKey: e.target.value })}
                        placeholder="Paste NanoBanana API Key..."
                        className="w-full bg-slate-950 border border-slate-700 text-white px-4 py-3 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition font-mono text-xs shadow-inner"
                      />
                  </div>
               </div>
               <p className="text-[10px] text-slate-500 mt-3 italic leading-relaxed">Save these credentials to power advanced Conversational AI features and professional campaigns using your own limits.</p>
            </div>

            {mailerXpData && (
              <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800 space-y-4 animate-in slide-in-from-top-2">
                 <div>
                    <div className="text-[10px] font-black text-slate-500 uppercase mb-2 flex items-center justify-between">
                       <span>MailerXP Lists ({mailerXpData.lists.length})</span>
                       <Check size={12} className="text-emerald-500" />
                    </div>
                    <div className="flex flex-wrap gap-2">
                       {mailerXpData.lists.slice(0, 5).map((l, i) => (
                           <span key={i} className="px-2 py-1 bg-slate-800 text-slate-300 text-[10px] rounded border border-slate-700 truncate max-w-[120px]">{l?.name || l?.title || 'List '+i}</span>
                       ))}
                       {mailerXpData.lists.length === 0 && <span className="text-xs text-slate-600 italic">No lists found.</span>}
                    </div>
                 </div>
                 <div>
                    <div className="text-[10px] font-black text-slate-500 uppercase mb-2 flex items-center justify-between">
                       <span>MailerXP Segments ({mailerXpData.segments.length})</span>
                       <Check size={12} className="text-emerald-500" />
                    </div>
                    <div className="flex flex-wrap gap-2">
                       {mailerXpData.segments.slice(0, 5).map((s, i) => (
                           <span key={i} className="px-2 py-1 bg-slate-800 text-slate-300 text-[10px] rounded border border-slate-700 truncate max-w-[120px]">{s?.name || s?.title || 'Segment '+i}</span>
                       ))}
                       {mailerXpData.segments.length === 0 && <span className="text-xs text-slate-600 italic">No segments found.</span>}
                    </div>
                 </div>
              </div>
            )}
            
            <div className="pt-4 border-t border-slate-800">
               <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center">
                 <Zap size={14} className="mr-2 text-amber-400" /> Global Postback URL (S2S Tracking)
               </label>
               <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800 text-slate-300 font-mono text-xs break-all shadow-inner relative group">
                  https://xpgo.link/api/pb?cid=&#123;clickid&#125;&payout=&#123;payout&#125;
                  <button 
                     onClick={() => navigator.clipboard.writeText(`https://xpgo.link/api/pb?cid={clickid}&payout={payout}`)}
                     className="absolute top-2 right-2 p-1.5 bg-slate-800 hover:bg-indigo-600 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-all shadow-md"
                  >
                     Copy
                  </button>
               </div>
               <p className="text-[10px] text-slate-500 mt-3 italic leading-relaxed">Paste this into ClickBank, MaxBounty, or CPALead. Change the &#123;bracket&#125; macros to match your network's specific macros if needed.</p>
            </div>
            
            <div className="pt-4">
              <button 
                onClick={() => onSave(formData)}
                className="w-full py-5 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white font-black rounded-2xl shadow-xl shadow-indigo-500/20 transform hover:-translate-y-1 active:scale-95 transition-all text-xl"
              >
                Update Ninja Profile
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AgentInitModal = ({ isOpen, onClose, onInitialize }) => {
  const [step, setStep] = useState(1);
  const [isScouting, setIsScouting] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    network: 'JVZoo',
    affiliateLink: '',
    salesPageLink: '',
    niche: '',
    payout: '',
    payoutModel: 'CPS/Sale',
    gravityScore: '',
    networkEpc: '',
    targetGeo: '',
    allowedTraffic: []
  });



  const toggleTrafficType = (type) => {
      setFormData(prev => ({
          ...prev,
          allowedTraffic: prev.allowedTraffic.includes(type) 
              ? prev.allowedTraffic.filter(t => t !== type)
              : [...prev.allowedTraffic, type]
      }));
  };

  const handleScoutOffer = async () => {
    setIsScouting(true);
    try {
        const payload = {
            link: formData.salesPageLink || formData.affiliateLink || formData.niche,
            affiliateLink: formData.affiliateLink,
            salesPageLink: formData.salesPageLink,
            context: formData.niche,
            network: formData.network,
            payout: formData.payout,
            payoutModel: formData.payoutModel,
            gravityScore: formData.gravityScore,
            networkEpc: formData.networkEpc,
            targetGeo: formData.targetGeo,
            allowedTraffic: formData.allowedTraffic
        };
        const res = await fetch('/api/wizard/scout', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const data = await res.json();
        setAuditData(data);
        
        // Auto-select primary recommended strategy by default
        const initialStrats = data.strategies || [];
        const primaryStrat = initialStrats.find(s => s.trafficSource === data.recommendedTraffic) || initialStrats[0];
        setFormData(prev => ({
            ...prev,
            selectedStrategies: primaryStrat ? [primaryStrat] : (initialStrats.length > 0 ? [initialStrats[0]] : [])
        }));
        
        setStep(2);
    } catch (err) {
        console.error(err);
        // Fallback if network fails
        const fallbackAudit = {
            offerGrade: 'A-',
            recommendedFunnel: 'Landing Page',
            recommendedTraffic: 'General',
            coachAdvice: 'Unable to scout link fully, but we can still build a funnel based on your niche context.',
            strategies: [
              {
                id: "native_advertorial",
                title: "Option 1: News Advertorial Review",
                trafficSource: "Native Ads",
                funnelType: "advertorial",
                preSellType: "advertorial",
                angle: "Review Journal / Secret Hack",
                description: "Pre-sell visitors using an authoritative review journal styled as a news article. Highly effective for warm demographic engagement via Native Networks."
              },
              {
                id: "google_search_bridge",
                title: "Option 2: Google Search Intent Bridge",
                trafficSource: "Google Ads",
                funnelType: "landing",
                preSellType: "product_presell",
                angle: "Direct Niche Value Comparison",
                description: "Capture high-intent searchers on Google Ads with an educational problem-solution bridge page highlighting direct features and user benefits."
              },
              {
                id: "facebook_vsl_bridge",
                title: "Option 3: Meta Curiosity VSL Bridge",
                trafficSource: "Facebook Ads",
                funnelType: "vsl",
                preSellType: "vsl_bridge",
                angle: "Discovery Breakthrough Teaser",
                description: "Stop social media scrollers with a curiosity-focused video bridge page leading to a high-converting email opt-in capture popup modal."
              }
            ]
        };
        setAuditData(fallbackAudit);
        setFormData(prev => ({
            ...prev,
            selectedStrategies: [fallbackAudit.strategies[0]]
        }));
        setStep(2);
    }
    setIsScouting(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md animate-in fade-in duration-300">
      <div className="bg-slate-900 border border-slate-700/50 rounded-[3rem] w-full max-w-xl shadow-[0_0_100px_rgba(79,70,229,0.2)] overflow-hidden relative flex flex-col transform animate-in zoom-in-95 duration-300">
        <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500"></div>
        
        <div className="p-10">
          <div className="flex items-center justify-between mb-10">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-indigo-600/20 text-indigo-400 rounded-2xl border border-indigo-500/20 shadow-inner">
                <Bot size={28} />
              </div>
              <div>
                <h3 className="text-2xl font-black text-white tracking-tight uppercase">Campaign AI Coach</h3>
                <p className="text-[10px] text-slate-500 font-black uppercase tracking-[0.2em] mt-0.5">New Campaign Setup</p>
              </div>
            </div>
            <button onClick={onClose} className="p-3 text-slate-500 hover:text-white hover:bg-slate-800 rounded-2xl transition-all">
              <X size={24} />
            </button>
          </div>
          
          <div className="space-y-8">
            {step === 1 && (
              <div className="space-y-8 animate-in fade-in">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Campaign Name</label>
                    <input 
                      type="text" 
                      value={formData.name} 
                      onChange={e => setFormData({...formData, name: e.target.value})} 
                      placeholder="e.g. Diet Protocol" 
                      className="w-full bg-slate-950/50 border border-slate-700/50 text-white px-6 py-4 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all font-bold text-sm shadow-inner placeholder:text-slate-700" 
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Affiliate Network</label>
                    <div className="relative group">
                      <select 
                        value={formData.network} 
                        onChange={e => setFormData({...formData, network: e.target.value})}
                        className="w-full bg-slate-950/50 border border-slate-700/50 text-white px-6 py-4 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all font-bold text-sm shadow-inner appearance-none cursor-pointer"
                      >
                        <option value="JVZoo">JVZoo</option>
                        <option value="ClickBank">ClickBank</option>
                        <option value="WarriorPlus">WarriorPlus</option>
                        <option value="DigiStore24">DigiStore24</option>
                        <option value="CPALead">CPALead</option>
                        <option value="MaxBounty">MaxBounty</option>
                        <option value="BuyGoods">BuyGoods</option>
                        <option value="ClickFunnels">ClickFunnels</option>
                        <option value="Custom/Other">Custom / Other</option>
                      </select>
                      <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500 group-hover:text-indigo-400 transition-colors">
                         <ChevronDown size={16} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 flex items-center">
                    <Link size={12} className="mr-2 text-indigo-400" /> Affiliate / Monetization Link
                  </label>
                  <input 
                    type="url" 
                    value={formData.affiliateLink} 
                    onChange={e => setFormData({...formData, affiliateLink: e.target.value})} 
                    placeholder="https://..." 
                    className="w-full bg-slate-950/50 border border-slate-700/50 text-white px-6 py-4 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all font-mono text-sm shadow-inner placeholder:text-slate-700" 
                  />
                </div>

                <div className="space-y-3">
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 flex items-center justify-between">
                    <span className="flex items-center"><ExternalLink size={12} className="mr-2 text-rose-400" /> Direct Sales Page URL (Optional)</span>
                    <span className="text-rose-400 normal-case text-[9px] font-bold bg-rose-500/10 px-2 py-0.5 rounded-full border border-rose-500/20">Highly Recommended</span>
                  </label>
                  <input 
                    type="url" 
                    value={formData.salesPageLink} 
                    onChange={e => setFormData({...formData, salesPageLink: e.target.value})} 
                    placeholder="https://... (Direct path to sales page)" 
                    className="w-full bg-slate-950/50 border border-slate-700/50 text-white px-6 py-4 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all font-mono text-sm shadow-inner placeholder:text-slate-700" 
                  />
                  <p className="text-[9px] text-slate-500 ml-1 italic leading-tight">If your affiliate link has redirects or bot-protection, providing the direct sales page helps the AI "Scout" see exactly what we're promoting.</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 flex items-center">
                      <DollarSign size={12} className="mr-2 text-emerald-400" /> Expected Payout ($)
                    </label>
                    <input 
                      type="number" 
                      value={formData.payout} 
                      onChange={e => setFormData({...formData, payout: e.target.value})} 
                      placeholder="e.g. 50" 
                      className="w-full bg-slate-950/50 border border-slate-700/50 text-white px-6 py-4 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all font-bold text-sm shadow-inner placeholder:text-slate-700" 
                    />
                  </div>

                  <div className="space-y-3">
                    <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 flex items-center">
                      <Target size={12} className="mr-2 text-indigo-400" /> Payout Model
                    </label>
                    <div className="relative group">
                      <select 
                        value={formData.payoutModel} 
                        onChange={e => setFormData({...formData, payoutModel: e.target.value})}
                        className="w-full bg-slate-950/50 border border-slate-700/50 text-white px-6 py-4 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all font-bold text-sm shadow-inner appearance-none cursor-pointer"
                      >
                        <option value="CPS/Sale">CPS (Cost Per Sale)</option>
                        <option value="CPA/SOI">CPA (Single Opt-In / SOI)</option>
                        <option value="CPA/DOI">CPA (Double Opt-In / DOI)</option>
                        <option value="CPA/Lead">CPA (General Lead Gen)</option>
                        <option value="RevShare">RevShare (%)</option>
                        <option value="Trial">Free Trial / CCT</option>
                      </select>
                      <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500 group-hover:text-indigo-400 transition-colors">
                         <ChevronDown size={16} />
                      </div>
                    </div>
                  </div>

                  {formData.network === 'ClickBank' && (
                  <div className="space-y-3 animate-in fade-in zoom-in-95">
                    <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 flex items-center">
                      <TrendingUp size={12} className="mr-2 text-rose-400" /> Gravity Score
                    </label>
                    <input 
                      type="number" 
                      value={formData.gravityScore} 
                      onChange={e => setFormData({...formData, gravityScore: e.target.value})} 
                      placeholder="e.g. 150" 
                      className="w-full bg-slate-950/50 border border-slate-700/50 text-white px-6 py-4 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all font-bold text-sm shadow-inner placeholder:text-slate-700" 
                    />
                  </div>
                  )}

                  {(formData.network === 'MaxBounty' || formData.network === 'ClickBank') && (
                  <div className="space-y-3 animate-in fade-in zoom-in-95">
                    <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 flex items-center">
                      <TrendingUp size={12} className="mr-2 text-indigo-400" /> Network EPC ($)
                    </label>
                    <input 
                      type="number" 
                      step="0.01"
                      value={formData.networkEpc} 
                      onChange={e => setFormData({...formData, networkEpc: e.target.value})} 
                      placeholder="e.g. 1.25" 
                      className="w-full bg-slate-950/50 border border-slate-700/50 text-white px-6 py-4 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all font-bold text-sm shadow-inner placeholder:text-slate-700" 
                    />
                  </div>
                  )}
                </div>
                
                <div className="space-y-3">
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 flex items-center">
                    <Globe size={12} className="mr-2 text-sky-400" /> Target GEO & Language (Optional)
                  </label>
                  <input 
                    type="text" 
                    value={formData.targetGeo} 
                    onChange={e => setFormData({...formData, targetGeo: e.target.value})} 
                    placeholder="e.g. US/UK only, Spanish, Tier 1, Global" 
                    className="w-full bg-slate-950/50 border border-slate-700/50 text-white px-6 py-4 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all text-sm shadow-inner placeholder:text-slate-700" 
                  />
                </div>

                <div className="space-y-3">
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 flex items-center">
                    <Zap size={12} className="mr-2 text-amber-400" /> Allowed Traffic Types (Select all that apply)
                  </label>
                  <div className="flex flex-wrap gap-2">
                      {TRAFFIC_TYPES.map(type => (
                          <button
                              key={type}
                              onClick={() => toggleTrafficType(type)}
                              className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all border ${formData.allowedTraffic.includes(type) ? 'bg-amber-500/20 text-amber-400 border-amber-500/50 shadow-[0_0_10px_rgba(245,158,11,0.2)]' : 'bg-slate-900 text-slate-500 border-slate-700 hover:border-slate-600 hover:text-slate-400'}`}
                          >
                              {type}
                          </button>
                      ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 flex items-center justify-between">
                    <span className="flex items-center"><Target size={12} className="mr-2 text-indigo-400" /> Offer Context / Niche</span>
                    <span className="text-indigo-400 normal-case text-[9px] font-bold bg-indigo-500/10 px-2 py-0.5 rounded-full border border-indigo-500/20">Critical for AI Precision</span>
                  </label>
                  <textarea 
                    value={formData.niche} 
                    onChange={e => setFormData({...formData, niche: e.target.value})} 
                    placeholder="e.g. High-ticket weight loss supplement targeting women 40+ in US/UK market. Key benefit: No exercise required." 
                    className="w-full bg-slate-950/50 border border-slate-700/50 text-white px-6 py-4 rounded-2xl focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all text-sm h-32 resize-none custom-scrollbar shadow-inner placeholder:text-slate-700 leading-relaxed" 
                  />
                </div>

                <div className="pt-6 flex space-x-4">
                  <button 
                    onClick={() => onInitialize(null)} 
                    className="flex-1 py-5 bg-slate-800/50 hover:bg-slate-800 text-slate-400 hover:text-white font-black rounded-2xl transition-all border border-slate-700/30 hover:border-slate-600 uppercase tracking-widest text-xs active:scale-95"
                  >
                    Skip Intel
                  </button>
                  <button 
                    onClick={handleScoutOffer} 
                    disabled={(!formData.affiliateLink && !formData.niche) || isScouting} 
                    className="flex-[2.5] py-5 bg-gradient-to-tr from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-black rounded-2xl shadow-[0_20px_40px_rgba(79,70,229,0.3)] hover:shadow-[0_25px_50px_rgba(79,70,229,0.4)] transition-all flex justify-center items-center active:scale-95 uppercase tracking-widest text-sm group"
                  >
                    {isScouting ? <Loader2 className="animate-spin size-5 mr-3" /> : <Search size={20} className="mr-3 group-hover:scale-110 transition-transform" />} Scout Offer & Strategy
                  </button>
                </div>
              </div>
            )}

            {step === 2 && auditData && (
              <div className="space-y-6 animate-in slide-in-from-right-4">
                <div className="flex items-center space-x-3 mb-4">
                  <Star size={24} className="text-amber-400" />
                  <h3 className="text-2xl font-black text-white tracking-tight">Offer Analysis & Strategy</h3>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-5 bg-slate-950/50 border border-slate-700/50 rounded-2xl flex flex-col justify-center items-center text-center shadow-inner">
                    <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest mb-2">Offer Grade</div>
                    <div className="text-4xl font-black text-emerald-400">{auditData.offerGrade || 'A-'}</div>
                  </div>
                  <div className="p-5 bg-slate-950/50 border border-slate-700/50 rounded-2xl flex flex-col justify-center items-center text-center shadow-inner">
                    <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest mb-2">Difficulty</div>
                    <div className="text-xl font-black text-white">{auditData.difficulty || 'Medium'}</div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center text-[10px] text-indigo-400 font-black uppercase tracking-widest mb-1">
                    <Zap size={12} className="mr-1.5" /> Selected Strategies (Multi-Select for A/B Testing)
                  </div>
                  <div className="grid grid-cols-1 gap-4 max-h-[300px] overflow-y-auto pr-1">
                    {(auditData.strategies || [
                      {
                        id: "native_advertorial",
                        title: "Option 1: News Advertorial Review",
                        trafficSource: "Native Ads",
                        funnelType: "advertorial",
                        preSellType: "advertorial",
                        angle: "Review Journal / Secret Hack",
                        description: "Pre-sell visitors using an authoritative review journal styled as a news article. Highly effective for warm demographic engagement via Native Networks."
                      },
                      {
                        id: "google_search_bridge",
                        title: "Option 2: Google Search Intent Bridge",
                        trafficSource: "Google Ads",
                        funnelType: "landing",
                        preSellType: "product_presell",
                        angle: "Direct Niche Value Comparison",
                        description: "Capture high-intent searchers on Google Ads with an educational problem-solution bridge page highlighting direct features and user benefits."
                      },
                      {
                        id: "facebook_vsl_bridge",
                        title: "Option 3: Meta Curiosity VSL Bridge",
                        trafficSource: "Facebook Ads",
                        funnelType: "vsl",
                        preSellType: "vsl_bridge",
                        angle: "Discovery Breakthrough Teaser",
                        description: "Stop social media scrollers with a curiosity-focused video bridge page leading to a high-converting email opt-in capture popup modal."
                      }
                    ]).map((strat) => {
                      const isSelected = (formData.selectedStrategies || []).some(s => s.id === strat.id);
                      const isRecommended = strat.trafficSource === auditData.recommendedTraffic;
                      
                      return (
                        <div 
                          key={strat.id}
                          onClick={() => {
                            setFormData(prev => {
                              const list = prev.selectedStrategies || [];
                              const exists = list.some(s => s.id === strat.id);
                              let newList;
                              if (exists) {
                                if (list.length <= 1) return prev; // Keep at least one strategy
                                newList = list.filter(s => s.id !== strat.id);
                              } else {
                                newList = [...list, strat];
                              }
                              return { ...prev, selectedStrategies: newList };
                            });
                          }}
                          className={`p-5 rounded-2xl border transition-all duration-200 cursor-pointer relative overflow-hidden flex flex-col justify-between ${
                            isSelected 
                              ? 'bg-indigo-600/10 border-indigo-500 shadow-[0_0_20px_rgba(99,102,241,0.15)]' 
                              : 'bg-slate-950/40 border-slate-800 hover:border-slate-700/80 hover:bg-slate-900/40'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <input 
                                type="checkbox" 
                                checked={isSelected}
                                readOnly
                                className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-700 bg-slate-900 pointer-events-none accent-indigo-500" 
                              />
                              <h4 className="text-xs font-black text-white tracking-tight">{strat.title}</h4>
                            </div>
                            {isRecommended && (
                              <span className="text-[8px] font-black uppercase tracking-widest text-amber-400 bg-amber-400/10 border border-amber-400/20 px-2 py-0.5 rounded-full animate-pulse">
                                🤖 Coach's Pick
                              </span>
                            )}
                          </div>
                          <p className="text-[10px] text-slate-400 leading-relaxed mb-3">{strat.description}</p>
                          <div className="flex flex-wrap gap-2 mt-auto">
                            <span className="text-[8px] font-extrabold text-indigo-300 bg-indigo-500/10 border border-indigo-500/20 px-2 py-0.5 rounded-md">
                              Traffic: {strat.trafficSource}
                            </span>
                            <span className="text-[8px] font-extrabold text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-md">
                              Funnel: {strat.funnelType}
                            </span>
                            <span className="text-[8px] font-extrabold text-purple-300 bg-purple-500/10 border border-purple-500/20 px-2 py-0.5 rounded-md">
                              Angle: {strat.angle}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="p-5 bg-slate-950/50 border border-slate-700/50 rounded-2xl shadow-inner relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-4 opacity-10"><Bot size={40} className="text-slate-500" /></div>
                  <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest mb-3 flex items-center"><MessageCircle size={12} className="mr-1.5" /> Coach's Advice</div>
                  <p className="text-sm text-slate-300 italic leading-relaxed relative z-10">"{auditData.coachAdvice || 'This looks like a solid offer to promote. We will generate a high-converting funnel tailored to this niche.'}"</p>
                </div>

                <div className="pt-6 flex space-x-4">
                  <button 
                    onClick={() => setStep(1)} 
                    className="flex-1 py-5 bg-slate-800/50 hover:bg-slate-800 text-slate-400 hover:text-white font-black rounded-2xl transition-all border border-slate-700/30 hover:border-slate-600 uppercase tracking-widest text-xs active:scale-95"
                  >
                    Back to Edit
                  </button>
                  <button 
                    onClick={() => onInitialize({...formData, auditData})} 
                    className="flex-[2.5] py-5 bg-gradient-to-tr from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white font-black rounded-2xl shadow-[0_20px_40px_rgba(16,185,129,0.3)] hover:shadow-[0_25px_50px_rgba(16,185,129,0.4)] transition-all flex justify-center items-center active:scale-95 uppercase tracking-widest text-sm group"
                  >
                    <Rocket size={20} className="mr-3 group-hover:animate-bounce" /> Continue & Build Funnel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="px-10 py-6 bg-slate-950/50 border-t border-slate-800/50 flex items-center justify-center space-x-8">
            <div className="flex items-center text-[10px] text-slate-500 font-bold uppercase tracking-wider">
                <ShieldCheck size={14} className="mr-2 text-emerald-500" /> Compliance Ready
            </div>
            <div className="flex items-center text-[10px] text-slate-500 font-bold uppercase tracking-wider">
                <Zap size={14} className="mr-2 text-amber-500" /> Neural Optimization
            </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [advice, setAdvice] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ clicks: 0, conversions: 0, optins: 0, roi: '0%' });
  const [linksData, setLinksData] = useState([]);
  const [currentView, setCurrentView] = useState('landing');
  const [importingShareId, setImportingShareId] = useState(null);
  const [importUsername, setImportUsername] = useState('');
  const [importAffiliateLink, setImportAffiliateLink] = useState('');
  const [userJVZooLink, setUserJVZooLink] = useState(localStorage.getItem('ninja_jvzoo_link') || '');
  const [partnerHubFunnelType, setPartnerHubFunnelType] = useState('recommendation');
  // Partner Hub content is now edited via the AI Coach editor (like all other campaigns)
  const [userProfile, setUserProfile] = useState(() => {
    try {
      const saved = localStorage.getItem('ninja_profile');
      return saved ? JSON.parse(saved) : { affiliateId: '', mailerXpApi: '', customDomains: [] };
    } catch (e) {
      console.error("Failed to parse ninja_profile:", e);
      return { affiliateId: '', mailerXpApi: '', customDomains: [] };
    }
  });
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [analyzingCampaign, setAnalyzingCampaign] = useState(null);
  const [isAgentInitModalOpen, setIsAgentInitModalOpen] = useState(false);
  const [isSpyModalOpen, setIsSpyModalOpen] = useState(false);
  const [isScoutModalOpen, setIsScoutModalOpen] = useState(false);
  const [partnerHubHtml, setPartnerHubHtml] = useState(null);
  const [expandedCampaigns, setExpandedCampaigns] = useState({});


  const toggleCampaignExpanded = (campaignId) => {
    setExpandedCampaigns(prev => ({
      ...prev,
      [campaignId]: !prev[campaignId]
    }));
  };

  const handleUpdateProfile = async (newProfile) => {
    if (!newProfile) return;
    const cleanedProfile = {
      affiliateId: newProfile.affiliateId || '',
      mailerXpApi: newProfile.mailerXpApi || '',
      customDomains: Array.isArray(newProfile.customDomains) ? newProfile.customDomains : []
    };
    setUserProfile(cleanedProfile);
    localStorage.setItem('ninja_profile', JSON.stringify(cleanedProfile));

    if (user && user.email) {
      try {
        await fetch('/api/profile', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: user.email,
            ...cleanedProfile
          })
        });
      } catch (err) {
        console.error("Failed to save profile to server:", err);
      }
    }

    if (newProfile.geminiApiKey !== undefined) {
      setGeminiApiKey(newProfile.geminiApiKey || '');
      localStorage.setItem('gemini_api_key', newProfile.geminiApiKey || '');
    }
    if (newProfile.openaiApiKey !== undefined) {
      setOpenaiApiKey(newProfile.openaiApiKey || '');
      localStorage.setItem('openai_api_key', newProfile.openaiApiKey || '');
    }
    if (newProfile.nanobananaApiKey !== undefined) {
      setNanobananaApiKey(newProfile.nanobananaApiKey || '');
      localStorage.setItem('nanobanana_api_key', newProfile.nanobananaApiKey || '');
    }

    setIsProfileModalOpen(false);
  };

  useEffect(() => {
    localStorage.setItem('ninja_jvzoo_link', userJVZooLink);
  }, [userJVZooLink]);

  useEffect(() => {
    localStorage.setItem('ninja_profile', JSON.stringify(userProfile));
  }, [userProfile]);

  useEffect(() => {
    const handleUrlRoute = async () => {
      const path = window.location.pathname.substring(1).split('/')[0].toLowerCase();
      if (path === 'settings') {
        setCurrentView('settings');
        return;
      }
      if (path === 'dashboard' || path === '') {
        setCurrentView('dashboard');
        return;
      }
      const reserved = ['', 'dashboard', 'login', 'signup', 'landing', 'api', 'r', 'u', 'settings'];
      if (path && !reserved.includes(path)) {
          console.log("[ROUTER] Detected potential username path:", path);
          try {
              const res = await fetch(`/api/campaigns/public/${path}`);
              const data = await res.json();
              if (data.success && data.html) {
                  setPartnerHubHtml(data.html);
                  setCurrentView('partner-hub');
                  if (data.title) document.title = data.title;
              }
          } catch (e) {
              console.error("[ROUTER ERROR]", e);
          }
      }
    };
    handleUrlRoute();
    window.addEventListener('popstate', handleUrlRoute);
    return () => window.removeEventListener('popstate', handleUrlRoute);
  }, []);

  const handleCopyLink = (text, label) => {
    navigator.clipboard.writeText(text);
    alert(`${label} copied to clipboard!\n\nURL: ${text}`);
  };

  // --- Campaign Wizard State ---
  const brandPresets = [
    {
      name: '🍃 Wellness',
      primary: '#10b981',
      secondary: '#06b6d4',
      background: '#061512',
      buttonStyle: 'rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/20 font-bold',
      desc: 'Health, Natural, Wealth'
    },
    {
      name: '🤖 AI & Tech',
      primary: '#6366f1',
      secondary: '#ec4899',
      background: '#0f172a',
      buttonStyle: 'rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-500/20 font-bold',
      desc: 'Digital, SaaS, Modern'
    },
    {
      name: '📈 Finance',
      primary: '#ef4444',
      secondary: '#f59e0b',
      background: '#0f0f10',
      buttonStyle: 'rounded-xl bg-rose-600 hover:bg-rose-700 text-white shadow-lg shadow-rose-650/20 font-bold',
      desc: 'Trading, Business, Forex'
    },
    {
      name: '👑 Premium',
      primary: '#d97706',
      secondary: '#f59e0b',
      background: '#1a1813',
      buttonStyle: 'rounded-xl bg-amber-500 hover:bg-amber-600 text-neutral-900 shadow-lg shadow-amber-500/20 font-extrabold',
      desc: 'High-Ticket, Coaching'
    },
    {
      name: '🍊 Sunset',
      primary: '#f97316',
      secondary: '#ec4899',
      background: '#0c0a09',
      buttonStyle: 'rounded-xl bg-orange-600 hover:bg-orange-700 text-white shadow-lg shadow-orange-600/20 font-bold',
      desc: 'Viral, Fun, E-commerce'
    }
  ];

  const backgroundPresets = [
    { name: '🌑 Dark Slate', hex: '#0f172a' },
    { name: '🌑 Pitch Black', hex: '#09090b' },
    { name: '🌑 Dark Charcoal', hex: '#1e293b' },
    { name: '🌑 Deep Forrest', hex: '#061512' },
    { name: '☀️ Clean White', hex: '#ffffff' }
  ];

  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);

  // Custom API Keys
  const [openaiApiKey, setOpenaiApiKey] = useState(() => localStorage.getItem('openai_api_key') || '');
  const [geminiApiKey, setGeminiApiKey] = useState(() => localStorage.getItem('gemini_api_key') || '');
  const [nanobananaApiKey, setNanobananaApiKey] = useState(() => localStorage.getItem('nanobanana_api_key') || '');

  // Coach Chat Room State
  const [coachChatHistory, setCoachChatHistory] = useState([]);
  const [coachInput, setCoachInput] = useState('');
  const [isCoachThinking, setIsCoachThinking] = useState(false);

  const getAuthHeaders = () => {
    const headers = { 'Content-Type': 'application/json' };
    if (geminiApiKey) headers['x-gemini-key'] = geminiApiKey;
    if (openaiApiKey) headers['x-openai-key'] = openaiApiKey;
    if (nanobananaApiKey) headers['x-nanobanana-key'] = nanobananaApiKey;
    return headers;
  };
  const [campaignData, setCampaignData] = useState({
    name: 'Auto-Campaign',
    network: 'JVZoo',
    username: 'ninja_master',
    postbackUrl: '',
    affiliateLink: '',
    niche: '',
    stealthRedirect: true,
    customDomain: 'xpgo.link',
    payout: '',
    payoutModel: 'CPS/Sale',
    gravityScore: '',
    networkEpc: '',
    targetGeo: '',
    preSellType: 'advertorial',
    allowedTraffic: [],
    selectedStrategies: []
  });
  
  const [isScouting, setIsScouting] = useState(false);
  const [isAnglesGenerating, setIsAnglesGenerating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [angleOptions, setAngleOptions] = useState([]);
  const [auditResults, setAuditResults] = useState(null);
  const [campaignBrandStyles, setCampaignBrandStyles] = useState({
    primary: '#6366f1',
    secondary: '#ec4899',
    background: '#0f172a',
    buttonStyle: 'rounded-xl bg-indigo-600 text-white font-bold'
  });
  const [isColorsAutoDetected, setIsColorsAutoDetected] = useState(false);
  const [isColorsExpanded, setIsColorsExpanded] = useState(false);
  const [selectedTrafficSource, setSelectedTrafficSource] = useState(null);
  const [selectedAngle, setSelectedAngle] = useState(null);
  const [copyVariants, setCopyVariants] = useState(null);
  const [isChatBubbleOpen, setIsChatBubbleOpen] = useState(false);
  const [builderHistory, setBuilderHistory] = useState([
    { role: 'assistant', content: 'Coach activated. How can I help you scale today?' }
  ]);
  const [livePages, setLivePages] = useState({});
  const [activePageId, setActivePageId] = useState('funnel_map');
  const [adStudioPlatform, setAdStudioPlatform] = useState(null);
  const [chatPrompt, setChatPrompt] = useState('');
  const [isChatting, setIsChatting] = useState(false);
  const [scrapedText, setScrapedText] = useState('');
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [customDomainStatus, setCustomDomainStatus] = useState(null);

  const fetchData = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setLinksData([]);
        setStats({ clicks: 0, conversions: 0, optins: 0, roi: '0.00%' });
        return;
      }

      const idToken = await currentUser.getIdToken();
      const authHeaders = {
        Authorization: `Bearer ${idToken}`
      };

      const [statsRes, campaignsRes] = await Promise.all([
        fetch('/api/stats', { headers: authHeaders }),
        fetch('/api/campaigns', { headers: authHeaders })
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      } else {
        console.warn('Failed to fetch stats:', statsRes.statusText);
      }

      if (campaignsRes.ok) {
        const campaignsData = await campaignsRes.json();
        if (Array.isArray(campaignsData)) {
          setLinksData(campaignsData);
        } else {
          console.error('Campaigns data is not an array:', campaignsData);
          setLinksData([]);
        }
      } else {
        console.warn('Failed to fetch campaigns:', campaignsRes.statusText);
      }
    } catch (err) {
      console.error('Error in fetchData:', err);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isUserDropdownOpen && !e.target.closest('#user-dropdown-container')) {
        setIsUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isUserDropdownOpen]);

  useEffect(() => {
    fetchData();

    // Check for custom domain client-side fallback
    const host = window.location.hostname.toLowerCase();
    const DEFAULT_PLATFORM_HOSTS = ['xpgo.link', 'www.xpgo.link', 'affiliateflow.ai', 'www.affiliateflow.ai', 'localhost', '127.0.0.1'];
    const isCustomDomain = !DEFAULT_PLATFORM_HOSTS.includes(host) && !host.endsWith('.web.app') && !host.endsWith('.firebaseapp.com');

    if (isCustomDomain) {
      console.log("[CUSTOM DOMAIN DETECTED CLIENT-SIDE]", host);
      fetch(`/api/campaigns/by-domain/${host}`)
        .then(async res => {
          const data = await res.json().catch(() => null);
          if (res.ok && data && data.username && data.slug) {
            const subPath = window.location.pathname.replace(/^\//, '');
            const target = subPath ? `/u/${data.username}/${data.slug}/${subPath}` : `/u/${data.username}/${data.slug}`;
            window.location.replace(target + window.location.search);
          } else {
            setCustomDomainStatus({
              status: res.status,
              error: data?.error || (res.status === 404 ? 'No landing page configured for this URL.' : 'Domain is not active yet.'),
              hostname: host
            });
          }
        })
        .catch(err => {
          console.error("[DOMAIN RESOLUTION FAILED]", err);
          setCustomDomainStatus({
            status: 500,
            error: 'Failed to resolve custom domain',
            hostname: host
          });
        });
    }

    const path = window.location.pathname;
    if (path.startsWith('/share/')) {
        const shareId = path.split('/')[2];
        if (shareId) setImportingShareId(shareId);
        window.history.replaceState({}, document.title, "/");
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log("[AUTH] State changed:", user ? user.email : "no-user");
      setUser(user);
      
      if (user) {
        // Only switch to dashboard if we are on the landing page
        setCurrentView(prev => (prev === 'landing' ? 'dashboard' : prev));
        fetchData();
        
        // Auto-fill ID if not set
        setCampaignData(prev => {
          if (!prev || prev.username) return prev;
          try {
              const autoId = user.displayName?.toLowerCase().replace(/\s+/g, '_') || user.email?.split('@')[0] || 'ninja_user';
              return { ...prev, username: autoId };
          } catch (e) {
              return prev;
          }
        });
      } else {
        // Only switch to landing if we are not in a public view
        setCurrentView(prev => (prev === 'partner-hub' ? 'partner-hub' : 'landing'));
      }
      setLoading(false);
    }, (error) => {
      console.error("[AUTH ERROR]", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      if (user && user.email) {
        try {
          const res = await fetch(`/api/profile/${encodeURIComponent(user.email)}`);
          if (res.ok) {
            const data = await res.json();
            if (data) {
              setUserProfile({
                affiliateId: data.affiliateId || '',
                mailerXpApi: data.mailerXpApi || '',
                customDomains: Array.isArray(data.customDomains) ? data.customDomains : (data.customDomain ? [data.customDomain] : [])
              });
            }
          }
        } catch (e) {
          console.error("Failed to fetch profile from server:", e);
        }
      }
    };
    fetchProfile();
  }, [user]);

  useEffect(() => {
    if (Array.isArray(linksData)) {
      const activePh = linksData.find(c => c.isPartnerHub && c.isActive === true) 
        || linksData.find(c => c.isPartnerHub);
      if (activePh) {
        console.log("[PARTNER HUB] Loaded from server:", activePh);
        if (activePh.affiliateLink) setUserJVZooLink(activePh.affiliateLink);
        if (activePh.funnelType) setPartnerHubFunnelType(activePh.funnelType);
      }
    }
  }, [linksData]);

  const [mailerXpLists, setMailerXpLists] = useState([]);
  const [mailerXpSegments, setMailerXpSegments] = useState([]);

  const openWizard = async (initialData = null) => {
    setWizardStep(1);
    setCampaignData({ 
        name: initialData?.name || ('Auto-Campaign ' + Math.floor(Math.random() * 1000)), 
        network: initialData?.network || 'JVZoo',
        username: userProfile.affiliateId || 'ninja_master',
        postbackUrl: '',
        affiliateLink: initialData?.affiliateLink || '', 
        niche: initialData?.niche || '',
        slug: 'promo-' + Math.floor(Math.random() * 10000), 
        stealthRedirect: true,
        customNetwork: initialData?.customNetwork || '',
        customDomain: 'xpgo.link',
        angle: null,
        mailerXpListId: '',
        mailerXpSegmentId: '',
        captureMethod: 'mailerxp',
        customHtmlForm: '',
        payout: initialData?.payout || '',
        payoutModel: initialData?.payoutModel || 'CPS/Sale',
        networkEpc: initialData?.networkEpc || '',
        targetGeo: initialData?.targetGeo || ''
    });
    setCoachChatHistory([]);
    setCoachInput('');
    setIsCoachThinking(false);
    setIsColorsAutoDetected(false);
    setIsColorsExpanded(false);
    setSelectedAngle(null);
    setAngleOptions([]);
    setAuditResults(null);
    setCopyVariants(null);
    setLivePages({}); setActivePageId('main');
    setBuilderHistory([{ role: 'assistant', content: 'Coach activated. Paste your offer link in Step 1 and I will perform a Deep Scout audit for you.' }]);
    
    if (userProfile.mailerXpApi) {
        try {
            const [listsRes, segmentsRes] = await Promise.all([
                fetch('/api/mailerxp/lists', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ apiKey: userProfile.mailerXpApi }) }),
                fetch('/api/mailerxp/segments', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ apiKey: userProfile.mailerXpApi }) })
            ]);
            const lists = await listsRes.json();
            const segments = await segmentsRes.json();
            setMailerXpLists(Array.isArray(lists) ? lists : (lists.data || lists.lists || []));
            setMailerXpSegments(Array.isArray(segments) ? segments : (segments.data || segments.segments || []));
        } catch (e) {
            console.error("Failed to load MailerXP metadata", e);
        }
    }

    setIsWizardOpen(true);
  };

  const handleCoachSendMessage = async (forcedText = null, visibleText = null) => {
    const textToSend = forcedText !== null ? forcedText : coachInput;
    if (!textToSend.trim()) return;

    // Detect if message is a URL
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const urlMatches = textToSend.match(urlRegex);
    if (urlMatches && urlMatches.length > 0) {
      const detectedUrl = urlMatches[0];
      setCampaignData(prev => ({ 
        ...prev, 
        affiliateLink: detectedUrl, 
        salesPageUrl: detectedUrl 
      }));
    }

    const displayText = visibleText !== null ? visibleText : textToSend;
    const newHistory = [...coachChatHistory, { sender: 'user', text: displayText }];
    setCoachChatHistory(newHistory);
    setCoachInput('');
    setIsCoachThinking(true);

    try {
      const apiHistory = [...coachChatHistory, { sender: 'user', text: textToSend }];
      const res = await fetch('/api/wizard/chat', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ messages: apiHistory })
      });
      const data = await res.json();
      setCoachChatHistory([...newHistory, { sender: 'coach', text: data.reply || "I'm on it! Tell me more or click a step below." }]);
    } catch (e) {
      console.error(e);
      setCoachChatHistory([...newHistory, { sender: 'coach', text: "Apologies, Ninja, I encountered a communication drift. Let's try again!" }]);
    } finally {
      setIsCoachThinking(false);
    }
  };

  const handleStartCoachingSession = async () => {
    // 1. Run the deep scout audit to scrape target offer, extract niche context and brand colors
    const results = await handleScout();
    
    const trafficRec = results ? results.traffic : 'Google Ads';
    const funnelRec = results ? results.funnel : 'Advertorial';
    const adviceRec = results ? results.advice : 'Failed to generate specific advice, but this is a solid traffic/funnel combo for most niches.';
    const gradeRec = results ? results.offerGrade : 'B';
    const difficultyRec = results ? results.difficulty : 'Medium';
    
    // 2. Trigger conversational AI Coach onboarding chat
    const visibleMessage = `I've configured my offer details for "${campaignData?.name || 'Auto-Campaign'}". Let's run a strategic research audit on this offer!`;
    const triggerPrompt = `[SYSTEM EVENT: CAMPAIGN INITIALIZED]
Please analyze this campaign and offer context, perform deep research on it, and generate a highly strategic audit report.

We ran a Deep Scout audit scan which extracted these initial metrics:
- Recommended Primary Traffic: "${trafficRec}"
- Recommended Funnel Structure: "${funnelRec}"
- Scout Recommendation Advice: "${adviceRec}"
- Offer Grade Rating: "${gradeRec}"
- Promotional Difficulty: "${difficultyRec}"

Campaign Context Details:
- Campaign Name: "${campaignData?.name || 'Untitled Campaign'}"
- Affiliate / Sales Link: "${campaignData.affiliateLink || 'None'}"
- Niche/Description: "${campaignData.niche || 'General Niche'}"
- Network: "${campaignData.network || 'JVZoo'}"
- Payout: "${campaignData.payout || 'N/A'}"
- Payout Model: "${campaignData.payoutModel || 'CPS/Sale'}"
- EPC: "${campaignData.networkEpc || 'N/A'}"
- GEO Target: "${campaignData.targetGeo || 'Global'}"

Please structure your response exactly as a world-class strategic marketing coach:
1. 🎓 **Deep Offer Context & Niche Analysis**: Give a sharp market breakdown based on real competitor data and your search grounding. Who is the target buyer, what is their primary emotional pain point, and how does this offer solve it?
2. 🏆 **Strategic Offer Grade**: Highlight your rating (Grade ${gradeRec}, Difficulty ${difficultyRec}) with 2-3 clear bullet points explaining this rating based on the EPC ($${campaignData.networkEpc || '0.00'}), payout, and promotional difficulty.
3. 🛣️ **Recommended Traffic Channel & Strategy**: Explicitly recommend **${trafficRec}** as the primary traffic source for this offer. Explain *why* this specific traffic source is perfect for this offer, how to approach it, and what funnel strategy (**${funnelRec}**) goes with it.
4. 💬 **Strategic Call-to-Action**: Ask a smart question to prompt my feedback on this traffic source recommendation before we lock in our traffic channel and generate funnel blueprints!`;

    handleCoachSendMessage(triggerPrompt, visibleMessage);
  };

  const handleSelectAngle = (angle) => {
    setSelectedAngle(angle);
    setCampaignData(prev => ({ ...prev, angle: angle }));
  };

  const handleGenerateRecommendationFunnel = async (reset = false) => {
    if (!userJVZooLink) {
        alert("Please enter your monetization link first!");
        return;
    }
    setIsLaunching(true);
    try {
      const res = await fetch('/api/campaigns/generate-recommendation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          username: userProfile.affiliateId || 'ninja_partner',
          affiliateLink: userJVZooLink,
          funnelType: partnerHubFunnelType,
          reset: reset
        })
      });
      const data = await res.json();
      if (data.success) {
        const username = userProfile.affiliateId || 'ninja_partner';
        const shortLink = `${window.location.host}/${username}`;
        const tipMsg = reset
          ? 'Layout successfully restored to defaults!'
          : 'Tip: Use the AI Coach Editor to customize the headline, VSL, and copy!';
        alert(`✅ PARTNER HUB DEPLOYED!\n\nYour live link:\n${shortLink}\n\n${tipMsg}`);
        fetchData();
      } else {
        alert("Error generating funnel: " + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error(err);
      alert("Failed to generate funnel.");
    }
    setIsLaunching(false);
  };

  const handlePreviewRecommendationFunnel = () => {
    // Create a temporary form to POST to the preview endpoint in a new tab
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/api/campaigns/preview-recommendation';
    form.target = '_blank';

    const fields = {
      affiliateLink: userJVZooLink || '#'
    };

    Object.keys(fields).forEach(key => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = fields[key];
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  const handleCloneOfficialFunnel = async () => {
    if (!userJVZooLink) {
        alert("Please enter target monetization link first!");
        return;
    }
    setIsLaunching(true);
    try {
      const res = await fetch('/api/campaigns/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          sourceId: 'ninja-official-template', 
          newUsername: userProfile.affiliateId || 'ninja_partner',
          newAffiliateLink: userJVZooLink
        })
      });
      const data = await res.json();
      if (data.success) {
        alert("Funnel Cloned Successfully!");
        fetchData();
        setCurrentView('dashboard');
      } else {
        alert("Error cloning funnel: " + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error(err);
      alert("Failed to clone funnel.");
    }
    setIsLaunching(false);
  };

  const handleEditCampaign = (campaign) => {
    if (!campaign) return;
    
    // Auto-construct pages mapping from pageVariants if missing (for legacy or partner hub campaigns)
    let campaignPages = campaign.pages;
    if (!campaignPages && campaign.pageVariants && Array.isArray(campaign.pageVariants)) {
      campaignPages = {};
      campaign.pageVariants.forEach(v => {
        if (v && v.name) {
          campaignPages[v.name] = {
            headline: 'Special Offer',
            body: 'Discover the exact system...',
            cta: 'Claim Access Now',
            html: v.html_content || ''
          };
        }
      });
    }

    setCampaignData({
      id: campaign?.id || '',
      name: campaign?.name || 'Auto-Campaign',
      network: campaign.network || 'ClickBank',
      username: campaign.username || 'ninja_master',
      postbackUrl: campaign.postbackUrl || '',
      affiliateLink: campaign.affiliateLink || '',
      niche: campaign.niche || '',
      slug: campaign.slug || '',
      stealthRedirect: campaign.stealthRedirect !== false,
      customNetwork: campaign.customNetwork || '',
      customDomain: campaign.customDomain || 'xpgo.link',
      captureMethod: campaign.captureMethod || 'mailerxp',
      customHtmlForm: campaign.customHtmlForm || '',
      mailerXpListId: campaign.mailerXpListId || '',
      mailerXpSegmentId: campaign.mailerXpSegmentId || '',
      pages: campaignPages || null,
      funnelArchitecture: campaign.funnelArchitecture || null,
      pageVariants: campaign.pageVariants || [],
      isPartnerHub: campaign.isPartnerHub || false
    });
    
    // Safety check for page variants
    if (campaign.pageVariants && Array.isArray(campaign.pageVariants) && campaign.pageVariants.length > 0) {
      const restoredPages = {};
      campaign.pageVariants.forEach(v => {
          if (v && v.name) restoredPages[v.name] = v.html_content || '';
      });
      setLivePages(restoredPages);
      setActivePageId('funnel_map');
      setIsWizardOpen(false);
      setCurrentView('agent-workspace');
    } else {
      setLivePages({});
      setActivePageId('funnel_map');
      setIsWizardOpen(false);
      setCurrentView('agent-workspace');
    }
    
    setSelectedAngle({ id: 1, title: 'Existing Campaign', description: 'Loaded from database.' });
    setAngleOptions([]);
    setCopyVariants({ existing: true });
    if (campaign && campaign.pageVariants) {
      setBuilderHistory([{ role: 'assistant', content: `Hey Ninja! I've loaded the active funnel for "${campaign?.name || 'this campaign'}". What would you like me to tweak? I can change colors, rewrite copy, or even rebuild the whole thing! ⚡️` }]);
    }
    setCurrentView('agent-workspace');
    setIsWizardOpen(false); 
  };

  // Persist capture node configs from FunnelVisualizer to Firestore
  const handleSaveCaptureNodes = async (architecture) => {
    if (!campaignData?.id) return;
    try {
      await fetch(`/api/campaigns/${campaignData.id}/architecture`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ funnelArchitecture: { ...(campaignData.funnelArchitecture || {}), ...architecture } }),
      });
      setCampaignData(prev => ({ ...prev, funnelArchitecture: { ...(prev.funnelArchitecture || {}), ...architecture } }));
    } catch (err) {
      console.error('[handleSaveCaptureNodes] Failed:', err);
    }
  };

  // Compile a capture popup HTML block from a capture node config
  const buildCapturePopupHtml = (captureConfig = {}, campaignId = '') => {
    const headline = captureConfig.headline || 'Get Instant Access';
    const subtext  = captureConfig.subtext  || 'Enter your email to continue';
    const btnText  = captureConfig.btnText  || 'Yes, Give Me Access →';
    return `
<!-- Ninja Capture Popup -->
<div id="ninja-capture-modal" class="fixed inset-0 z-50 hidden items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md">
  <div class="relative w-full max-w-md bg-slate-900 border border-slate-700 text-white p-8 rounded-[2rem] shadow-2xl flex flex-col items-center text-center">
    <button onclick="document.getElementById('ninja-capture-modal').classList.replace('flex','hidden')" class="absolute top-4 right-4 text-slate-500 hover:text-white p-2 rounded-full hover:bg-slate-800">✕</button>
    <div class="w-16 h-16 bg-gradient-to-tr from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg mb-5">
      <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
    </div>
    <h3 class="text-2xl font-black text-white mb-3">${headline}</h3>
    <p class="text-slate-400 mb-6 leading-relaxed">${subtext}</p>
    <form onsubmit="handleNinjaCaptureOptin(event)" class="w-full space-y-3">
      <input type="email" id="ninja-capture-email" required placeholder="Your best email address..." class="w-full px-4 py-3 bg-slate-950 border border-slate-700 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 text-base"/>
      <button type="submit" class="w-full py-3.5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-black text-base rounded-xl shadow-lg transition hover:-translate-y-0.5 active:translate-y-0">${btnText}</button>
    </form>
    <p class="text-xs text-slate-600 mt-4">🔒 Privacy protected. No spam ever.</p>
  </div>
</div>
<script>
window.__NINJA_CAMPAIGN_ID__ = "${campaignId}";
function openNinjaCapturePopup() {
  var m = document.getElementById('ninja-capture-modal');
  if (m) { m.classList.remove('hidden'); m.classList.add('flex'); }
}
async function handleNinjaCaptureOptin(e) {
  e.preventDefault();
  var email = document.getElementById('ninja-capture-email').value.trim();
  if (!email) return;
  try { await fetch('/api/public/optin', { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ email, campaignId: window.__NINJA_CAMPAIGN_ID__, source: 'capture_popup' }) }); } catch(err) {}
  window.location.href = '{affiliate_link}';
}
</script>`;
  };

  const compileHTML = (copy, urgent = false, type = 'main', nextPath = null, customUrl = null, nextPageCopy = null, captureNextUrl = '{affiliate_link}') => {
    let bodyContent = '';
    const isLandingOrSqueeze = type === 'landing' || type === 'squeeze' || type.toLowerCase().includes('landing') || type.toLowerCase().includes('squeeze');
    const hasCapturePopup = isLandingOrSqueeze && nextPath === 'capture';
    
    // Determine the next destination URL based on the funnel sequence
    // Default fallback values if no nextPath provided
    let nextUrlPlaceholder = '{tracking_url}/capture'; 
    if (isLandingOrSqueeze || type === 'capture') nextUrlPlaceholder = '{affiliate_link}';
    
    let actualNextUrl = customUrl || (nextPath 
        ? (nextPath === 'offer' ? '{affiliate_link}' : `{tracking_url}/${nextPath}`) 
        : nextUrlPlaceholder);

    if (hasCapturePopup) {
        actualNextUrl = 'javascript:openCapturePopup()';
    }

    // If the AI generated pure HTML string, use it directly but update placeholders
    if (typeof copy === 'string') {
        bodyContent = copy;
    } else if (copy && typeof copy === 'object' && copy.html) {
        bodyContent = copy.html;
    } else if (copy && typeof copy === 'object' && copy.htmlCustom) {
        bodyContent = copy.htmlCustom;
    } else {
        const safeCopy = copy || {};
        if (type === 'capture') {
            bodyContent = `<h1 class="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight">${safeCopy.headline || 'Exclusive Access'}</h1><p class="text-xl text-slate-600 max-w-lg mx-auto">Enter your email below to unlock instant access to the strategy.</p><!-- OPTIN_FORM_START --><div class="max-w-md mx-auto bg-slate-50 p-6 rounded-2xl border border-slate-200 mt-8 shadow-inner"><input type="email" placeholder="Enter your best email..." class="w-full px-4 py-4 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 mb-4 text-lg"><button onclick="window.location.href='${actualNextUrl}'" class="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold text-xl py-4 rounded-xl shadow-[0_10px_20px_-10px_rgba(16,185,129,0.5)] transform transition hover:-translate-y-1">Send Me Access Now!</button></div><!-- OPTIN_FORM_END --><p class="text-xs text-slate-400 mt-4">We respect your privacy.</p>`;
        } else if (type === 'landing') {
            bodyContent = `<h1 class="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">${safeCopy.headline || 'Watch This Training'}</h1><div class="w-full aspect-video bg-slate-900 rounded-2xl overflow-hidden shadow-2xl relative my-8 group flex items-center justify-center border-4 border-slate-800"><div class="absolute inset-0 bg-slate-800 animate-pulse opacity-50"></div><div class="z-10 bg-rose-600 text-white rounded-full w-20 h-20 flex items-center justify-center opacity-80 group-hover:opacity-100 transition-opacity cursor-pointer shadow-[0_0_30px_rgba(225,29,72,0.6)]"><svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M8 5v14l11-7z"/></svg></div></div><h2 class="text-2xl font-bold text-slate-800 mb-4">Ready to start?</h2><ul class="text-left max-w-lg mx-auto space-y-3 text-slate-700 mb-8">${(safeCopy.body || '').split('\\n').filter(l => l.trim()).map(li => `<li class="flex items-start text-lg"><span class="text-emerald-500 mr-3 text-xl">✓</span><span>${li}</span></li>`).join('')}</ul><a href="${actualNextUrl}" class="inline-block bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold text-xl py-5 px-14 rounded-full shadow-[0_15px_30px_-10px_rgba(59,130,246,0.6)] transform transition hover:-translate-y-1 animate-bounce">${safeCopy.cta || 'Activate Now'}</a>`;
        } else {
            bodyContent = `${urgent ? '<div class="bg-rose-500 text-white font-bold py-2 rounded-lg animate-pulse mb-6">URGENT!</div>' : ''}<h1 class="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight">${safeCopy.headline || 'Premium Opportunity'}</h1><ul class="text-left max-w-lg mx-auto space-y-4 text-slate-700 mt-8 mb-10">${(safeCopy.body || '').split('\\n').filter(l => l.trim()).map(li => `<li class="flex items-start text-lg"><span class="text-emerald-500 mr-3 text-xl">✓</span><span>${li}</span></li>`).join('')}</ul><a href="${actualNextUrl}" class="inline-block ${urgent ? 'bg-gradient-to-r from-rose-500 to-rose-600' : 'bg-gradient-to-r from-emerald-500 to-emerald-600'} text-white font-bold text-xl py-4 px-12 rounded-full transform transition hover:-translate-y-1">${safeCopy.cta || 'Instant Access'}</a>`;
        }
    }

    // Replace common sequence placeholders in both generated and custom HTML
    bodyContent = bodyContent.split('{tracking_url}/bridge').join(actualNextUrl);
    bodyContent = bodyContent.split('{tracking_url}/capture').join(actualNextUrl);
    if (hasCapturePopup) {
        bodyContent = bodyContent.split('{affiliate_link}').join(actualNextUrl);
    } else {
        bodyContent = bodyContent.split('{affiliate_link}').join('{affiliate_link}'); // Ensure it stays as placeholder for final replacement
    }

    let captureModalHtml = '';
    if (hasCapturePopup) {
        const popupHeadline = nextPageCopy?.headline || nextPageCopy?.title || 'Unlock Instant Access';
        const popupDesc = nextPageCopy?.body || nextPageCopy?.subheadline || 'Enter your best email address below to secure your spot and redirect to the presentation.';
        const popupCta = nextPageCopy?.cta || 'Get Instant Access';
        const campaignId = campaignData?.id || '';

        let renderedHeadline = `<h3 class="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-slate-100 to-slate-300 tracking-tight leading-tight mb-4">${popupHeadline}</h3>`;
        const regForIndex = popupHeadline.toLowerCase().indexOf('register for');
        if (regForIndex !== -1) {
            const prefix = popupHeadline.substring(regForIndex, regForIndex + 12).trim();
            const suffix = popupHeadline.substring(regForIndex + 12).trim();
            renderedHeadline = `
            <div class="text-xs sm:text-sm font-black uppercase tracking-[0.2em] text-indigo-400 mb-2">${prefix}</div>
            <h3 class="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-slate-100 to-slate-300 tracking-tight leading-none mb-4">${suffix}</h3>
            `;
        }

        captureModalHtml = `
<!-- Capture Popup Modal -->
<div id="ninja-capture-modal" class="fixed inset-0 z-50 hidden items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md transition-all duration-300">
    <div class="absolute inset-0 cursor-default" onclick="closeNinjaModal()"></div>
    <div class="relative w-full max-w-lg bg-slate-900 border border-slate-800/80 text-white p-8 md:p-10 rounded-[2rem] shadow-2xl shadow-indigo-500/10 transform scale-95 transition-all duration-300 flex flex-col items-center text-center animate-in fade-in zoom-in-95 duration-200">
        <button onclick="closeNinjaModal()" class="absolute top-5 right-5 text-slate-400 hover:text-white transition-colors p-2 rounded-full hover:bg-slate-800">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
        </button>
        <div class="w-20 h-20 flex items-center justify-center mb-6">
            <img src="/Dragon-affiliateninja.png" alt="Logo" class="w-full h-full object-contain filter drop-shadow-[0_0_15px_rgba(99,102,241,0.5)]">
        </div>
        ${renderedHeadline}
        <p class="text-slate-400 text-base md:text-lg mb-8 leading-relaxed max-w-sm">
            ${popupDesc}
        </p>
        <form onsubmit="handleNinjaOptin(event)" class="w-full space-y-4">
            <div class="relative">
                <span class="absolute inset-y-0 left-0 flex items-center pl-4 text-slate-500">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
                </span>
                <input type="text" id="ninja-optin-name" required placeholder="Enter your full name..." class="w-full pl-12 pr-4 py-4 bg-slate-950/50 border border-slate-800 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-white placeholder-slate-500 transition-all text-lg shadow-inner">
            </div>
            <div class="relative">
                <span class="absolute inset-y-0 left-0 flex items-center pl-4 text-slate-500">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.206"></path></svg>
                </span>
                <input type="email" id="ninja-optin-email" required placeholder="Enter your best email address..." class="w-full pl-12 pr-4 py-4 bg-slate-950/50 border border-slate-800 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-white placeholder-slate-500 transition-all text-lg shadow-inner">
            </div>
            <button type="submit" id="ninja-optin-submit" class="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-extrabold text-xl py-4 rounded-2xl shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 transform transition hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-3">
                <span>${popupCta}</span>
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
            </button>
        </form>
        <p class="text-sm text-slate-500 mt-6 flex items-center justify-center gap-2">
            <svg class="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>
            We respect your privacy. Spam-free experience.
        </p>
    </div>
</div>

<script>
window.__NINJA_CAMPAIGN_ID__ = "${campaignId}";
function openCapturePopup() {
    const modal = document.getElementById('ninja-capture-modal');
    if (modal) {
        modal.classList.remove('hidden');
        modal.classList.add('flex');
    }
    try {
        const campaignId = window.__NINJA_CAMPAIGN_ID__ || '';
        if (campaignId) {
            fetch('/api/track/pageclick', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ campaignId: campaignId, pageName: 'landing' })
            });
        }
    } catch(e) {}
}

function closeNinjaModal() {
    const modal = document.getElementById('ninja-capture-modal');
    if (modal) {
        modal.classList.remove('flex');
        modal.classList.add('hidden');
    }
}

async function handleNinjaOptin(e) {
    e.preventDefault();
    const nameInput = document.getElementById('ninja-optin-name');
    const emailInput = document.getElementById('ninja-optin-email');
    const submitBtn = document.getElementById('ninja-optin-submit');
    if (!emailInput || !submitBtn) return;
    
    const name = nameInput ? nameInput.value.trim() : '';
    const email = emailInput.value.trim();
    if (!email) return;
    
    submitBtn.disabled = true;
    submitBtn.innerHTML = '<span class="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span> Processing...';
    
    const slug = window.location.pathname.split('/').pop() || '';
    
    try {
        await fetch('/api/public/optin', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: name, email: email, source: slug ? 'popup_' + slug : 'popup' })
        });
        if (slug) {
            await fetch('/api/track/optin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ slug: slug })
            });
        }
    } catch (err) {
        console.error('Lead error:', err);
    }
    
    window.location.href = '${captureNextUrl}';
}
</script>
`;
    }

    // If the body already contains a body tag, don't double wrap it
    if (bodyContent.toLowerCase().includes('<body')) {
        // Dynamic legal compliance footer injection in client-side compiler — only append if not already present
        if (!bodyContent.includes('<!-- Legal Compliance Footer -->')) {
            const usernameVal = campaignData?.username || userProfile?.username || 'partner';
            const footerHtml = `
<!-- Legal Compliance Footer -->
<footer class="mt-20 py-8 border-t border-slate-900/40 text-center text-xs text-slate-600 space-y-2">
    <p>© 2026 Affiliate Flow AI Network. All rights reserved.</p>
    <div class="flex justify-center space-x-6">
        <a href="/${usernameVal}/terms" class="hover:text-slate-400 transition">Terms of Service</a>
        <a href="/${usernameVal}/privacy" class="hover:text-slate-400 transition">Privacy Policy</a>
        <a href="https://affiliateflow.ai" target="_blank" class="hover:text-slate-400 transition">Powered by Affiliate Flow AI</a>
    </div>
    <p class="text-[10px] text-slate-700 max-w-xl mx-auto mt-4 leading-relaxed px-4">
        Disclaimer: We are an independent review and training portal. Earnings and results mentioned in training are illustrative only. Standard affiliate commission tracking is active on this domain.
    </p>
</footer>
`;
            const footerCloseIdx = bodyContent.toLowerCase().lastIndexOf('</body>');
            if (footerCloseIdx !== -1) {
                bodyContent = bodyContent.substring(0, footerCloseIdx) + footerHtml + bodyContent.substring(footerCloseIdx);
            } else {
                bodyContent = bodyContent + footerHtml;
            }
        }

        // Only append the capture popup modal if it's not already in the page code
        if (hasCapturePopup && !bodyContent.includes('<!-- Capture Popup Modal -->')) {
            const bodyCloseIdx = bodyContent.toLowerCase().lastIndexOf('</body>');
            if (bodyCloseIdx !== -1) {
                bodyContent = bodyContent.substring(0, bodyCloseIdx) + captureModalHtml + bodyContent.substring(bodyCloseIdx);
            } else {
                bodyContent = bodyContent + captureModalHtml;
            }
        }
        return bodyContent;
    }
    
    return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><script src="https://cdn.tailwindcss.com"></script><link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css" rel="stylesheet"></head><body class="bg-slate-100 font-sans p-0 md:p-12"><div class="max-w-4xl mx-auto bg-white p-6 md:p-14 rounded-none md:rounded-[2rem] shadow-2xl text-center border border-slate-50">${bodyContent}${hasCapturePopup ? captureModalHtml : ''}</div></body></html>`;
  };

  const normalizeTrafficSource = (source) => {
    if (!source) return 'Native Ads';
    const s = source.toLowerCase();
    if (s.includes('google')) return 'Google Ads';
    if (s.includes('facebook') || s.includes('meta')) return 'Facebook Ads';
    if (s.includes('tiktok')) return 'TikTok Ads';
    if (s.includes('youtube')) return 'YouTube Ads';
    if (s.includes('taboola')) return 'Taboola';
    if (s.includes('outbrain')) return 'Outbrain';
    if (s.includes('email')) return 'Email Marketing';
    if (s.includes('solo')) return 'Solo Ads';
    if (s.includes('seo') || s.includes('organic') || s.includes('search engine')) return 'Organic / SEO';
    if (s.includes('native') || s.includes('revcontent') || s.includes('adnow')) return 'Native Ads';
    return 'Google Ads'; // default fallback
  };

  const handleScout = async () => {
    if (!campaignData.affiliateLink) return null;
    setIsScouting(true);
    
    // Auto-generate slug from URL if empty
    if (!campaignData.slug) {
        try {
            const url = new URL(campaignData.affiliateLink.includes('://') ? campaignData.affiliateLink : `https://${campaignData.affiliateLink}`);
            const hostSlug = url.hostname.replace('www.', '').split('.')[0] + '-' + Math.random().toString(36).substring(2, 6);
            setCampaignData(prev => ({ ...prev, slug: hostSlug }));
        } catch (e) {
            setCampaignData(prev => ({ ...prev, slug: 'campaign-' + Math.random().toString(36).substring(2, 6) }));
        }
    }

    let finalResults = null;
    try {
      const res = await fetch('/api/wizard/scout', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...campaignData })
      });
      const data = await res.json();
      
      const normalizedTraffic = normalizeTrafficSource(data.recommendedTraffic);
      // Always set audit results, even if we use defaults on error
      const results = {
          traffic: normalizedTraffic,
          funnel: data.recommendedFunnel || 'Advertorial',
          advice: data.coachAdvice || 'Failed to generate specific advice, but this is a solid traffic/funnel combo for most niches.',
          offerGrade: data.offerGrade || 'B',
          difficulty: data.difficulty || 'Medium',
          brandStyles: data.brandStyles || null,
          strategies: data.strategies || []
      };
      
      setAuditResults(results);
      if (data.brandStyles) {
        setCampaignBrandStyles(data.brandStyles);
      }
      const isAuto = data.scoutMode && data.scoutMode.includes("DEEP AUDIT") && data.brandStyles;
      setIsColorsAutoDetected(!!isAuto);
      setIsColorsExpanded(!isAuto);

      const initialStrats = data.strategies || [];
      const primaryStrat = initialStrats.find(s => s.trafficSource === normalizedTraffic) || initialStrats[0];
      const defaultSelected = primaryStrat ? [primaryStrat] : (initialStrats.length > 0 ? [initialStrats[0]] : []);

      setCampaignData(prev => ({
        ...prev,
        preSellType: data.recommendedPreSellType || 'advertorial',
        selectedStrategies: defaultSelected
      }));
      setScrapedText(data.scrapedText || '');
      setSelectedTrafficSource(results.traffic);
      setWizardStep(2); 
      finalResults = results;
    } catch (err) { 
      console.error(err);
      // Fallback so user isn't stuck
      const fallbackStrategies = [
        {
          id: "native_advertorial",
          title: "Option 1: News Advertorial Review",
          trafficSource: "Native Ads",
          funnelType: "advertorial",
          preSellType: "advertorial",
          angle: "Review Journal / Secret Hack",
          description: "Pre-sell visitors using an authoritative review journal styled as a news article. Highly effective for warm demographic engagement via Native Networks."
        },
        {
          id: "google_search_bridge",
          title: "Option 2: Google Search Intent Bridge",
          trafficSource: "Google Ads",
          funnelType: "landing",
          preSellType: "product_presell",
          angle: "Direct Niche Value Comparison",
          description: "Capture high-intent searchers on Google Ads with an educational problem-solution bridge page highlighting direct features and user benefits."
        },
        {
          id: "facebook_vsl_bridge",
          title: "Option 3: Meta Curiosity VSL Bridge",
          trafficSource: "Facebook Ads",
          funnelType: "vsl",
          preSellType: "vsl_bridge",
          angle: "Discovery Breakthrough Teaser",
          description: "Stop social media scrollers with a curiosity-focused video bridge page leading to a high-converting email opt-in capture popup modal."
        }
      ];
      const fallbackResults = {
          traffic: 'Google Ads',
          funnel: 'Direct Landing',
          advice: 'The AI scout hit a snag, but you can still proceed! We recommend a standard Search to Landing flow.',
          offerGrade: 'N/A',
          difficulty: 'Normal',
          brandStyles: null,
          strategies: fallbackStrategies
      };
      setAuditResults(fallbackResults);
      setSelectedTrafficSource('Google Ads');
      setCampaignData(prev => ({
        ...prev,
        selectedStrategies: [fallbackStrategies[0]]
      }));
      setWizardStep(2);
      finalResults = fallbackResults;
    }
    setIsScouting(false);
    return finalResults;
  };

  const handleSelectTraffic = async (source, overrideAuditText = null) => {
    setSelectedTrafficSource(source);
    setAngleOptions([]);
    setIsAnglesGenerating(true);
    setWizardStep(3);
    try {
      const res = await fetch('/api/wizard/angles', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...campaignData, traffic: source, auditText: overrideAuditText || scrapedText })
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        // Map steps so that 'bridge' is converted to 'landing', and order matches Traffic -> Landing -> Capture -> Offer
        const mappedData = data.map(angle => {
          const steps = (angle.steps || []).map(s => s === 'bridge' ? 'landing' : s);
          // Reorder steps to ensure 'landing' is before 'capture' (Traffic -> Landing -> Capture -> Offer)
          const orderedSteps = [];
          if (steps.includes('landing')) orderedSteps.push('landing');
          if (steps.includes('capture')) orderedSteps.push('capture');
          return { ...angle, steps: orderedSteps.length > 0 ? orderedSteps : ['landing'] };
        });
        setAngleOptions(mappedData);

        // Dynamic Conversational Coach Recommendation Prompt
        const visibleTrigger = `I want to draft strategic blueprints for my campaign. What hooks and funnel architectures do you recommend?`;
        const systemPrompt = `[SYSTEM EVENT: STRATEGIC BLUEPRINTS GENERATED]
The following customized strategic marketing blueprints have been generated specifically for this offer:
${JSON.stringify(mappedData.map(a => ({ title: a.title, description: a.description, steps: a.steps })))}

Please review these options, and provide a highly personalized, natural, and conversational breakdown in the chat:
1. Propose these visual blueprints to the user, explaining why they are highly effective.
2. Explain exactly *which option you recommend and why* (e.g. should we warm up the user with a pre-sell/VSL first, or go with a direct capture redirect, depending on the payout amount $${campaignData.payout || '0.00'} and EPC $${campaignData.networkEpc || '0.00'}).
3. Detail which traffic strategies (TikTok, Facebook ads, native ads) are most optimal for these models.
4. Invite the user to look at the blueprint cards below, select their favorite angle hook, and click '🚀 Generate Funnel Assets' to code the landers!`;

        handleCoachSendMessage(systemPrompt, visibleTrigger);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) { 
        console.error(err);
        const fallback = [
            { id: 1, title: 'High Performance Path', description: 'Standard high-converting path directly to the offer.', steps: ['landing'] },
            { id: 2, title: 'Lead Magnet', description: 'Capture leads before forwarding to the offer.', steps: ['landing', 'capture'] }
        ];
        setAngleOptions(fallback);
    }
    setIsAnglesGenerating(false);
  };

  const handleGenerate = async () => {
    if (!selectedAngle) return;
    setGenerating(true);
    try {
        const res = await fetch('/api/wizard/generate-copy', {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ 
                link: campaignData.affiliateLink, 
                angle: selectedAngle, 
                traffic: selectedTrafficSource,
                auditText: scrapedText,
                niche: campaignData.niche,
                offer_preview_url: campaignData.offer_preview_url,
                offer_description: campaignData.offer_description,
                funnelType: selectedAngle.steps?.[0] || 'landing',
                network: campaignData.network,
                brandStyles: campaignBrandStyles,
                preSellType: campaignData.preSellType || 'advertorial'
            })
        });
        const data = await res.json();
        
        if (data.error || (!data.pages && !data.standard && !data.html)) {
            throw new Error("Failed to generate from AI: " + (data.error || "Unknown"));
        }

        const launchPages = {};
        if (data.pages) {
            Object.keys(data.pages).forEach(key => { launchPages[key] = compileHTML(data.pages[key], false, key); });
        } else {
            launchPages.main = compileHTML(data.standard || data, false, 'main');
        }
        setLivePages(launchPages);
        setCopyVariants(data);
        setCampaignData(prev => ({ 
            ...prev, 
            pages: data.pages || { main: data.standard || data } 
        }));
        setActivePageId('funnel_map');
        setIsWizardOpen(false);
        setCurrentView('agent-workspace');
    } catch (err) {
        console.error(err);
        setIsWizardOpen(false);
        setCurrentView('agent-workspace');
    }
    setGenerating(false);
  };

  const handleDuplicatePage = async (pageId, forcedId = null) => {
    const newPageId = forcedId || `${pageId}_copy_${Math.floor(Math.random() * 1000)}`;
    const pageHtml = livePages[pageId] || '';
    
    setLivePages(prev => ({ ...prev, [newPageId]: pageHtml }));
    
    // Resolve current nodes and edges
    let nodes = [];
    let edges = [];
    if (campaignData?.funnelArchitecture) {
      nodes = campaignData.funnelArchitecture.nodes || [];
      edges = campaignData.funnelArchitecture.edges || [];
    }
    
    // Find the original page node being duplicated
    const originalNodeId = `page_${pageId}`;
    const originalNode = nodes.find(n => n.id === originalNodeId || n.id === pageId);
    
    let updatedNodes = [...nodes];
    let updatedEdges = [...edges];
    
    if (originalNode) {
      const newNodeId = `page_${newPageId}`;
      const duplicateNode = {
        ...originalNode,
        id: newNodeId,
        position: {
          x: originalNode.position.x,
          y: originalNode.position.y + 180
        },
        data: {
          ...originalNode.data,
          label: newPageId,
          id: newNodeId
        }
      };
      
      updatedNodes.push(duplicateNode);
      
      // Clone all edges going INTO the original node
      const incomingEdges = edges.filter(e => e.target === originalNode.id);
      incomingEdges.forEach(e => {
        updatedEdges.push({
          id: `edge-dup-${e.source}-to-${newNodeId}-${Math.floor(Math.random() * 1000)}`,
          source: e.source,
          target: newNodeId,
          type: 'animated'
        });
      });
      
      // Clone all edges going OUT of the original node
      const outgoingEdges = edges.filter(e => e.source === originalNode.id);
      outgoingEdges.forEach(e => {
        updatedEdges.push({
          id: `edge-dup-${newNodeId}-to-${e.target}-${Math.floor(Math.random() * 1000)}`,
          source: newNodeId,
          target: e.target,
          type: 'animated'
        });
      });
    }
    
    // Trigger handleSaveArchitecture to fully compile the new page and persist everything!
    await handleSaveArchitecture(updatedNodes, updatedEdges, true, { [newPageId]: pageId });

    if (activePageId !== 'funnel_map') {
      setActivePageId(newPageId);
    }
  };

  const handleEditPage = (pageId) => {
    setActivePageId(pageId);
  };

  const handleAddPage = async (type, position = null, autoWire = null, forcedId = null) => {
    const actualType = (typeof type === 'string' && type) ? type : 'squeeze';
    const newPageId = forcedId || `${actualType}_${Math.floor(Math.random() * 1000)}`;
    const isCapture = actualType === 'capture';
    
    let template = '';
    if (actualType === 'advertorial') {
      template = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0">
    <script src="https://cdn.tailwindcss.com"></script>
    <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@400;700;900&display=swap" rel="stylesheet">
    <style>body { font-family: 'Outfit', sans-serif; }</style>
</head>
<body class="bg-slate-950 text-slate-300 min-h-screen pb-20">
    <nav class="bg-slate-900 border-b border-slate-850 py-4 px-6 mb-12">
        <div class="max-w-4xl mx-auto flex justify-between items-center">
            <span class="text-white font-black tracking-widest text-lg">DAILY INSIDER JOURNAL</span>
            <span class="text-xs text-amber-400 font-bold bg-amber-400/10 border border-amber-500/20 px-3 py-1 rounded-full uppercase tracking-wider">Sponsored Editorial Report</span>
        </div>
    </nav>
    <article class="max-w-3xl mx-auto px-6 space-y-8 bg-slate-900/40 p-8 sm:p-12 border border-slate-850 rounded-[2.5rem]">
        <div class="space-y-4">
            <h1 class="text-4xl sm:text-5xl font-black text-white leading-tight">
                How a Group of Stealth Affiliate Marketers Are Generating $1,500/Day Using This Little-Known On-Chain AI Engine
            </h1>
            <div class="flex items-center space-x-3 text-xs text-slate-500 font-bold pt-2 border-y border-slate-850 py-3">
                <span>By Ronald Vance</span>
                <span>•</span>
                <span>Updated: May 2026</span>
                <span>•</span>
                <span class="text-emerald-400">✓ Verified Editorial Quality</span>
            </div>
        </div>
        
        <p class="text-lg leading-relaxed text-slate-350">
            For years, starting an affiliate business meant dealing with complex tech, expensive hosting, and hours of writing copy. But recently, a stealth AI system has entered the market, rewriting the rules. Here is our investigative report on the ultimate digital side-hustle of the decade.
        </p>
        
        <div class="bg-slate-950/80 border border-slate-850 rounded-3xl p-6 my-8 space-y-4">
            <h3 class="text-white font-extrabold text-lg">💡 Executive Summary of Our Findings:</h3>
            <ul class="list-disc pl-5 space-y-2.5 text-sm">
                <li><strong class="text-white">Zero Technical Skill Needed:</strong> AI constructs high-converting marketing architectures with a single prompt.</li>
                <li><strong class="text-white">Built-in A/B Testing:</strong> Automatically sends traffic to the highest-performing templates.</li>
                <li><strong class="text-white">Global Edge Deployment:</strong> Extremely fast pages for high mobile conversions.</li>
            </ul>
        </div>
        <p class="text-lg leading-relaxed text-slate-300">
            Our audit verified that this framework yields up to a 34% surge in initial subscriber click-through rates. Click below to inspect the AI dashboard and claim your stealth partner account while pre-launch registration is active.
        </p>
        
        <div class="text-center pt-6">
            <a href="{affiliate_link}" class="inline-block bg-gradient-to-r from-amber-500 via-purple-600 to-indigo-600 hover:from-amber-400 hover:to-indigo-500 text-white font-black text-lg px-12 py-5 rounded-2xl transition shadow-xl shadow-amber-500/20 transform active:scale-95">
                Access the Ninja Engine &rarr;
            </a>
            <p class="text-slate-600 text-xs font-bold uppercase tracking-widest mt-4">Limited Access Slots Remaining for Today</p>
        </div>
    </article>
</body>
</html>`;
    } else if (actualType === 'listicle') {
      template = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0">
    <script src="https://cdn.tailwindcss.com"></script>
    <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@400;700;900&display=swap" rel="stylesheet">
    <style>body { font-family: 'Outfit', sans-serif; }</style>
</head>
<body class="bg-slate-950 text-slate-300 min-h-screen pb-20">
    <header class="max-w-4xl mx-auto px-6 pt-16 pb-8 text-center space-y-4">
        <div class="inline-flex items-center px-3.5 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-[10px] font-black uppercase tracking-[0.2em]">
            ★ RECOMMENDED TRAINING ★
        </div>
        <h1 class="text-4xl sm:text-5xl font-black text-white tracking-tight">
            Learn Affiliate Marketing with AI
        </h1>
        <p class="text-slate-400 text-lg max-w-2xl mx-auto leading-relaxed">
            I've personally configured this high-converting system. Watch the free training blueprint below to discover how stealth affiliate loops generate commissions automatically.
        </p>
    </header>
    
    <!-- VSL Video Section -->
    <div class="max-w-4xl mx-auto px-6 mb-12">
        <div class="bg-slate-900/60 border border-slate-800 rounded-[2.5rem] p-6 sm:p-8 space-y-8 shadow-2xl relative overflow-hidden">
            <div class="absolute inset-0 bg-gradient-to-br from-indigo-900/10 via-transparent to-transparent opacity-50"></div>
            <div class="aspect-video w-full bg-slate-900 rounded-[2.5rem] border border-slate-800 shadow-[0_0_80px_rgba(99,102,241,0.15)] overflow-hidden flex items-center justify-center relative">
                <div class="absolute inset-0 bg-gradient-to-br from-indigo-900/20 to-purple-900/20"></div>
                <div class="relative z-10 text-center space-y-4">
                    <div class="w-24 h-24 bg-white/5 rounded-full flex items-center justify-center mx-auto border border-white/10">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-12 h-12 text-white/40 ml-1">
                            <path d="M8 5v14l11-7z"/>
                        </svg>
                    </div>
                    <p class="text-slate-500 font-bold text-xs uppercase tracking-widest">Video Coming Soon</p>
                </div>
            </div>
            
            <div class="text-center pt-4 space-y-4">
                <div>
                    <a href="{affiliate_link_1}" class="inline-flex items-center justify-center bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-black text-lg px-12 py-5 rounded-2xl shadow-xl shadow-indigo-600/20 hover:shadow-indigo-600/35 transform transition hover:-translate-y-0.5 active:translate-y-0 active:scale-95 duration-200">
                        Start Free Training &rarr;
                    </a>
                </div>
                <p class="text-[10px] text-slate-500 uppercase tracking-widest font-black">100% Free Video Blueprint · Instant Access</p>
                
                <div class="pt-2">
                    <a href="{affiliate_link_2}" class="inline-flex items-center text-xs text-indigo-400 hover:text-indigo-300 font-bold hover:underline transition">
                        ⚡ Skip training? Click here to access the AI Builder Engine directly &rarr;
                    </a>
                </div>
            </div>
        </div>
    </div>
    
    <main class="max-w-4xl mx-auto px-6 space-y-8">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="bg-slate-900/50 border border-slate-850 p-6 rounded-2xl flex items-start gap-4">
                <span class="text-indigo-400 text-xl font-bold">🤖</span>
                <div>
                    <h3 class="font-bold text-white mb-1">AI-Powered Strategy</h3>
                    <p class="text-slate-400 text-xs leading-relaxed">Discover how modern neural models automate copywriting, funnels, and tracking.</p>
                </div>
            </div>
            <div class="bg-slate-900/50 border border-slate-850 p-6 rounded-2xl flex items-start gap-4">
                <span class="text-indigo-400 text-xl font-bold">📈</span>
                <div>
                    <h3 class="font-bold text-white mb-1">Stealth Traffic Loops</h3>
                    <p class="text-slate-400 text-xs leading-relaxed">Learn the exact systematic routing designed to optimize clicks and generate sales 24/7.</p>
                </div>
            </div>
        </div>
    
</main>
</body>
</html>`;
    } else if (actualType === 'oto') {
      template = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0">
    <script src="https://cdn.tailwindcss.com"></script>
    <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@400;700;900&display=swap" rel="stylesheet">
    <style>body { font-family: 'Outfit', sans-serif; }</style>
    <title>One Time Only Offer</title>
</head>
<body class="bg-[#020617] text-slate-350 min-h-screen flex items-center justify-center p-6 bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-rose-950/20 via-slate-950 to-black">
    <div class="max-w-3xl w-full text-center space-y-8 bg-[#0f172a] p-16 rounded-[2.5rem] border border-slate-800 shadow-2xl relative overflow-hidden">
        <div class="absolute -top-24 -left-24 w-48 h-48 bg-rose-600/10 rounded-full blur-3xl"></div>
        <div class="absolute -bottom-24 -right-24 w-48 h-48 bg-amber-600/10 rounded-full blur-3xl"></div>
        
        <div class="relative z-10 space-y-6">
            <div class="inline-flex items-center px-4 py-1.5 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-400 text-[10px] font-black uppercase tracking-[0.2em] animate-pulse">
                ⚠️ Wait! Don't Close This Page
            </div>
            <h1 class="text-4xl sm:text-5xl font-black text-white tracking-tight leading-tight bg-gradient-to-br from-white to-slate-400 bg-clip-text text-transparent">
                One Time Only Masterclass Upgrade
            </h1>
            <p class="text-slate-400 text-lg max-w-2xl mx-auto leading-relaxed">
                Accelerate your progress. Upgrade your order now to unlock the complete step-by-step blueprints, software templates, and direct coaching access.
            </p>
            
            <div class="pt-4 max-w-sm mx-auto space-y-6">
                <a href="{oto_yes_link}" class="block bg-gradient-to-r from-rose-600 to-rose-500 hover:from-rose-500 hover:to-rose-400 text-white font-black text-lg py-5 px-8 rounded-2xl shadow-xl shadow-rose-600/20 transform hover:-translate-y-0.5 transition active:scale-95 text-center flex items-center justify-center gap-3">
                    Yes, Upgrade My Order Now! →
                </a>
                <a href="{oto_no_link}" class="block text-slate-500 hover:text-slate-400 font-bold text-xs uppercase tracking-wider transition underline">
                    No thanks, I'll pass on this forever
                </a>
            </div>
        </div>
    </div>
</body>
</html>`;
    } else {
      template = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0">
    <script src="https://cdn.tailwindcss.com"></script>
    <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@400;700;900&display=swap" rel="stylesheet">
    <style>body { font-family: 'Outfit', sans-serif; }</style>
</head>
<body class="bg-[#020617] text-white min-h-screen flex items-center justify-center p-6">
    <div class="max-w-2xl w-full text-center space-y-8 bg-[#0f172a] p-16 rounded-[2.5rem] border border-slate-800 shadow-2xl relative overflow-hidden">
        <div class="absolute -top-24 -left-24 w-48 h-48 bg-indigo-600/20 rounded-full blur-3xl"></div>
        <div class="absolute -bottom-24 -right-24 w-48 h-48 bg-rose-600/20 rounded-full blur-3xl"></div>
        
        <div class="relative z-10 space-y-6">
            <div class="inline-flex items-center px-4 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-[10px] font-black uppercase tracking-[0.2em]">
                ${isCapture ? 'Exclusive Access' : 'Special Bonus Unlock'}
            </div>
            <h1 class="text-5xl font-black leading-tight bg-gradient-to-br from-white to-slate-400 bg-clip-text text-transparent">
                ${isCapture ? 'Join the Ninja Elite' : 'One More Step to Your Success'}
            </h1>
            <p class="text-slate-400 text-xl font-medium leading-relaxed">
                ${isCapture ? 'Enter your best email below to unlock the hidden high-ticket strategy that 99% of affiliates will never see.' : 'You are moments away from accessing the exact blueprint used to scale campaigns from zero to hero.'}
            </p>
            
            <div class="pt-4">
                ${isCapture ? `
                <div class="space-y-4 max-w-sm mx-auto">
                    <input type="email" placeholder="name@email.com" class="w-full bg-slate-900/50 border border-slate-700 px-8 py-5 rounded-2xl outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all text-white font-bold placeholder:text-slate-600">
                    <button class="w-full bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-600 hover:to-indigo-400 text-white font-black text-sm uppercase tracking-widest px-10 py-5 rounded-2xl transition shadow-xl shadow-indigo-600/20 transform active:scale-95">
                        Get Instant Access &rarr;
                    </button>
                </div>
                ` : `
                <a href="{affiliate_link}" class="inline-block bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 text-white font-black text-sm uppercase tracking-widest px-12 py-5 rounded-2xl transition shadow-xl shadow-emerald-500/20 transform active:scale-95">
                    Claim Access Now &rarr;
                </a>
                `}
            </div>
            
            <p class="text-[10px] text-slate-600 font-bold uppercase tracking-widest pt-4">
                ${isCapture ? 'Your privacy is safe with us. No spam, ever.' : 'Limited time offer. Terms and conditions apply.'}
            </p>
        </div>
    </div>
</body>
</html>`;
    }
    setLivePages(prev => ({ ...prev, [newPageId]: template }));
    
    const newPageObj = {
        headline: actualType === 'advertorial' ? 'How Stealth AI Affiliate Marketers Are Generating $1,500/Day' : (actualType === 'listicle' ? 'The 3 Best AI Affiliate Engines of 2026' : (actualType === 'oto' ? 'One Time Only Masterclass Upgrade' : (isCapture ? 'Exclusive Access' : 'Special Bonus Unlock'))),
        body: actualType === 'advertorial' ? 'For years, starting an affiliate business meant...' : (actualType === 'listicle' ? 'We evaluated performance, pricing, edge-hosting speed...' : (actualType === 'oto' ? 'Accelerate your progress. Upgrade your order now...' : (isCapture ? 'Enter your best email below...' : 'You are moments away...'))),
        cta: actualType === 'advertorial' ? 'Access the Ninja Engine' : (actualType === 'listicle' ? 'Claim Best Discount' : (actualType === 'oto' ? 'Yes, Upgrade My Order Now!' : (isCapture ? 'Get Instant Access' : 'Claim Access Now'))),
        html: template
    };

    setCampaignData(prev => {
        const currentArch = prev.funnelArchitecture || { nodes: [], edges: [] };
        const nodeExists = currentArch.nodes.some(n => n.id === `page_${newPageId}`);
        
        const newNode = {
            id: `page_${newPageId}`, type: 'custom', 
            position: position || { x: 500, y: 300 },
            data: {
                label: newPageId,
                subtext: autoWire ? 'Injected Stage' : actualType.charAt(0).toUpperCase() + actualType.slice(1) + ' Stage',
                stage: actualType, isPage: true
            }
        };

        let newEdges = [...currentArch.edges];
        if (autoWire) {
            const { source, target } = autoWire;
            newEdges = newEdges.filter(e => !(e.source === source && e.target === target));
            newEdges.push({
                id: `e_${source}_${newPageId}`, source: source, target: `page_${newPageId}`, 
                type: 'custom', animated: true, style: { stroke: '#6366f1', strokeWidth: 3 }
            });
            newEdges.push({
                id: `e_${newPageId}_${target}`, source: `page_${newPageId}`, target: target, 
                type: 'custom', animated: true, style: { stroke: '#10b981', strokeWidth: 3 }
            });
        }

        const currentVariants = prev.pageVariants || [];
        const updatedVariants = [...currentVariants, { name: newPageId, html_content: template }];

        const updatedCampaign = {
            ...prev,
            pages: {
                ...prev.pages,
                [newPageId]: newPageObj
            },
            pageVariants: updatedVariants,
            funnelArchitecture: {
                nodes: nodeExists ? currentArch.nodes : [...currentArch.nodes, newNode],
                edges: newEdges
            }
        };

        if (prev.id) {
            const campaignRef = doc(db, 'campaigns', prev.id);
            updateDoc(campaignRef, {
                pages: updatedCampaign.pages,
                pageVariants: updatedVariants,
                funnelArchitecture: updatedCampaign.funnelArchitecture,
                updatedAt: new Date().toISOString()
            }).then(() => {
                console.log("💾 Persistent change: Added new page saved to Firestore successfully.");
            }).catch(err => {
                console.error("Firestore persistence failed for addPage:", err);
            });
        }

        return updatedCampaign;
    });

    if (activePageId !== 'funnel_map') {
        setActivePageId(newPageId);
    }
  };

  const autoGenerateFunnel = async (linkOrNiche, trafficSource = 'General', funnelType = 'landing', network = 'JVZoo', metrics = {}, preScoutedData = null) => {
    setIsScouting(true);
    let scrapedText = '';
    let auditData = preScoutedData;

    const blueprint = selectedAngle || { title: 'High_Performance_Direct_Path', steps: ['landing'] };
    const finalFunnelType = funnelType || (blueprint.steps?.includes('capture') ? 'capture' : 'landing');

    if (!auditData) {
        setBuilderHistory(prev => [...prev, { role: 'assistant', content: `Scouting ${linkOrNiche} for ${trafficSource} traffic... 🕵️‍♂️` }]);
        try {
            const auditRes = await fetch('/api/wizard/scout', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ link: linkOrNiche, context: linkOrNiche, network: network, ...metrics })
            });
            auditData = await auditRes.json();
            scrapedText = auditData.scrapedText || '';
            
            // Output the brutal honesty to the chat window
            setBuilderHistory(prev => [...prev, { role: 'assistant', content: `**Scout Complete!**\n\n**Offer Grade:** ${auditData.offerGrade || 'N/A'}\n**Difficulty:** ${auditData.difficulty || 'N/A'}\n\n**Coach's Honest Advice:**\n${auditData.coachAdvice || 'Looks solid.'}` }]);
        } catch(e) {}
    } else {
        scrapedText = auditData.scrapedText || '';
        setBuilderHistory(prev => [...prev, { role: 'assistant', content: `**Analysis Loaded!**\n\n**Offer Grade:** ${auditData.offerGrade || 'N/A'}\n**Difficulty:** ${auditData.difficulty || 'N/A'}\n\n**Coach's Honest Advice:**\n${auditData.coachAdvice || 'Looks solid.'}` }]);
    }
    
    let finalAngle = { title: "Direct Approach", description: "Straight to the point", angle: "Scientific Discovery" };
    try {
        const anglesRes = await fetch('/api/wizard/angles', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ link: linkOrNiche, traffic: trafficSource, auditText: scrapedText, network: network, ...metrics })
        });
        const anglesData = await anglesRes.json();
        if (anglesData && anglesData.length > 0) {
            finalAngle = anglesData[0];
        }
    } catch(e) {}

    setIsScouting(false);
    setBuilderHistory(prev => [...prev, { role: 'assistant', content: 'Scout complete! Generating the funnel HTML now... ⚡' }]);
    setGenerating(true);

    const generatePayload = {
        network: network,
        customNetwork: '',
        customDomain: 'xpgo.link',
        angle: finalAngle,
        niche: metrics.niche || linkOrNiche, // Use the niche description if we have it!
        stealthRedirect: true,
        affiliateLink: linkOrNiche,
        ...metrics
    };
    
    setCampaignData(prev => ({ ...prev, ...generatePayload, slug: 'agent-' + Math.floor(Math.random() * 1000) }));

    // Build the explicit offer intelligence payload for the AI
    const offerIntelPayload = {
        link: linkOrNiche,
        angle: finalAngle,
        traffic: trafficSource,
        funnelType: finalFunnelType,
        network: network,
        auditText: scrapedText,
        brandStyles: auditData?.brandStyles || null,
        preSellType: campaignData.preSellType || 'advertorial',
        // CRITICAL: Explicit named fields so the server destructuring always finds them
        niche: metrics.niche || campaignData.niche || linkOrNiche,
        offer_preview_url: campaignData.offer_preview_url,
        offer_description: campaignData.offer_description,
        payout: metrics.payout || campaignData.payout || '',
        payoutModel: metrics.payoutModel || campaignData.payoutModel || 'CPS',
        gravityScore: metrics.gravityScore || campaignData.gravityScore || '',
        networkEpc: metrics.networkEpc || campaignData.networkEpc || '',
        targetGeo: metrics.targetGeo || campaignData.targetGeo || 'US',
        allowedTraffic: metrics.allowedTraffic || campaignData.allowedTraffic || '',
        salesPageLink: metrics.salesPageLink || campaignData.salesPageLink || '',
        campaignName: metrics?.name || campaignData?.name || '',
        strategies: metrics.selectedStrategies || campaignData.selectedStrategies || []
    };

    try {
        const res2 = await fetch('/api/wizard/generate-copy', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(offerIntelPayload)
        });
        const data2 = await res2.json();
        const launchPages = {};
        if (data2.pages) {
            Object.keys(data2.pages).forEach(key => { launchPages[key] = compileHTML(data2.pages[key], false, key); });
        } else {
            launchPages.main = compileHTML(data2.standard || data2, false, 'main');
        }
        setLivePages(launchPages);
        setCopyVariants(data2);

        // Pre-populate the correct parallel funnel architecture state to prevent it from resetting or turning into a sequential line
        const pageList = Object.keys(data2.pages || { main: data2.standard || data2 });
        const hasCapture = pageList.includes('capture');
        const initialNodes = [];
        const initialEdges = [];
        
        const currentStrategies = metrics.selectedStrategies || campaignData.selectedStrategies || [];
        
        if (currentStrategies && currentStrategies.length > 1) {
            currentStrategies.forEach((strat, idx) => {
              const yPos = 80 + idx * 160;
              const trafficNodeId = `traffic_${strat.trafficSource.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
              const pageName = strat.funnelType || 'landing';
              const pageNodeId = `page_${pageName}`;

              initialNodes.push({
                id: trafficNodeId,
                type: 'traffic',
                position: { x: 50, y: yPos },
                data: { label: strat.trafficSource }
              });

              initialNodes.push({
                id: pageNodeId,
                type: 'page',
                position: { x: 320, y: yPos },
                data: { label: pageName }
              });

              initialEdges.push({
                id: `edge-${trafficNodeId}-to-${pageNodeId}`,
                source: trafficNodeId,
                target: pageNodeId,
                type: 'animated'
              });

              if (hasCapture) {
                initialEdges.push({
                  id: `edge-${pageNodeId}-to-page_capture`,
                  source: pageNodeId,
                  target: 'page_capture',
                  type: 'animated'
                });
              } else {
                initialEdges.push({
                  id: `edge-${pageNodeId}-to-offer`,
                  source: pageNodeId,
                  target: 'offer',
                  type: 'animated'
                });
              }
            });

            if (hasCapture) {
              initialNodes.push({
                id: 'page_capture',
                type: 'capture',
                position: { x: 590, y: 160 },
                data: { label: 'capture' }
              });
              initialEdges.push({
                id: 'edge-capture-to-offer',
                source: 'page_capture',
                target: 'offer',
                type: 'animated'
              });
            }

            initialNodes.push({
              id: 'offer',
              type: 'offer',
              position: { x: hasCapture ? 860 : 590, y: 160 },
              data: { label: network || 'Offer', affiliateLink: linkOrNiche }
            });
        } else {
            // General sequential flow
            // Start Traffic Node
            initialNodes.push({
              id: 'traffic',
              type: 'traffic',
              position: { x: 50, y: 150 },
              data: { label: trafficSource }
            });

            // Horizontal sequential pages
            pageList.forEach((pageName, idx) => {
              initialNodes.push({
                id: `page_${pageName}`,
                type: 'page',
                position: { x: 320 + idx * 280, y: 150 },
                data: { label: pageName }
              });
            });

            // End Offer Node
            initialNodes.push({
              id: 'offer',
              type: 'offer',
              position: { x: 320 + pageList.length * 280, y: 150 },
              data: { label: network || 'Offer', affiliateLink: linkOrNiche }
            });

            // Sequential connections
            initialEdges.push({
              id: 'edge-traffic-to-landing',
              source: 'traffic',
              target: `page_${pageList[0]}`,
              type: 'animated'
            });

            for (let i = 0; i < pageList.length - 1; i++) {
              initialEdges.push({
                id: `edge-page-${pageList[i]}-to-${pageList[i+1]}`,
                source: `page_${pageList[i]}`,
                target: `page_${pageList[i+1]}`,
                type: 'animated'
              });
            }

            initialEdges.push({
              id: 'edge-page-to-offer',
              source: `page_${pageList[pageList.length - 1]}`,
              target: 'offer',
              type: 'animated'
            });
        }

        const generatedArchitecture = { nodes: initialNodes, edges: initialEdges };

        setCampaignData(prev => ({ 
            ...prev, 
            pages: data2.pages || { main: data2.standard || data2 },
            funnelArchitecture: generatedArchitecture
        }));
        setActivePageId('funnel_map');
        setIsWizardOpen(false);
        setCurrentView('agent-workspace');
        setBuilderHistory(prev => [...prev, { role: 'assistant', content: 'Funnel built! Take a look on the right. Want me to change any colors or tweak the copy?' }]);
    } catch (e) {
        setBuilderHistory(prev => [...prev, { role: 'assistant', content: 'Oops, I hit a snag generating the HTML. Try again?' }]);
    }
    setGenerating(false);
  };

  const handleBuilderChat = async (directMessage = null) => {
    const messageToSend = directMessage || chatPrompt;
    if (!messageToSend) return;
    setIsChatting(true);
    const newHistory = [...builderHistory, { role: 'user', content: messageToSend }];
    setBuilderHistory(newHistory);
    setChatPrompt('');
    try {
      const res = await fetch('/api/coach/builder-chat', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          history: newHistory, 
          currentHtml: livePages[activePageId], 
          stats: stats, 
          campaigns: linksData 
        })
      });
      const data = await res.json();
      if (data.html) {
        setLivePages(prev => ({...prev, [activePageId]: data.html}));
        
        // Also persist this custom edited HTML to campaignData.pages to prevent architecture save overwrites
        const updatedPages = { ...(campaignData.pages || {}), [activePageId]: data.html };
        setCampaignData(prev => ({
          ...prev,
          pages: updatedPages
        }));
        
        if (campaignData.id) {
          try {
            const campaignRef = doc(db, 'campaigns', campaignData.id);
            await updateDoc(campaignRef, {
              pages: updatedPages,
              updatedAt: new Date().toISOString()
            });
            console.log("💾 Persisted custom landing page HTML edit to Firestore campaigns.");
          } catch (err) {
            console.error("Error saving custom HTML edit to Firestore campaigns:", err);
          }
        }
      }
      setBuilderHistory([...newHistory, { role: 'assistant', content: data.reply || 'Code updated.' }]);
      if (data.action === 'GENERATE_FUNNEL' && data.payload?.affiliateLink) {
          autoGenerateFunnel(
            data.payload.affiliateLink, 
            data.payload.trafficSource || campaignData.selectedTraffic, 
            data.payload.funnelType || campaignData.selectedFunnel, 
            data.payload.network || campaignData.network,
            { ...campaignData, ...data.payload }
          );
      }
    } catch (e) {
      console.error('Builder error:', e);
      setBuilderHistory(prev => [...prev, { role: 'assistant', content: "Something went wrong in the neural engine. Let's try again." }]);
    } finally {
      setIsChatting(false);
    }
  };

   const handleSaveArchitecture = async (nodes, edges, persist = true, newlyCreatedPages = null) => {
    if (!campaignData.id) return;
    
    // Sanitize data (strip functions and React elements that Firestore can't handle)
    const sanitizedNodes = nodes.map(({ data, ...n }) => {
        const { onEdit, onDuplicate, onCycleStage, onRename, onChangeStage, onChangeTrafficSource, onOpenAdStudio, initialLink, onRemove, icon, ...cleanData } = data || {};
        return { ...n, data: cleanData };
    });
    
    const sanitizedEdges = edges.map(({ data, ...e }) => {
        const { onDelete, onInsert, ...cleanData } = data || {};
        return { ...e, data: cleanData };
    });

    const newLivePages = { ...livePages };
    let updatedPageVariants = [...(campaignData.pageVariants || [])];
    
    // Combine existing pages with any newly created ones (e.g. from duplication)
    const allPagesData = { ...(campaignData.pages || {}) };
    if (newlyCreatedPages) {
        Object.entries(newlyCreatedPages).forEach(([nId, oData]) => {
            // If oData is a string, it's an originalId to copy from
            if (typeof oData === 'string' && allPagesData[oData]) {
                allPagesData[nId] = allPagesData[oData];
            } else {
                // Otherwise it's already the page data object
                allPagesData[nId] = oData;
            }
        });
    }

    // CRITICAL: Update livePages HTML to respect the new sequence defined by edges
    if (Object.keys(allPagesData).length > 0) {
        Object.keys(allPagesData).forEach(pageKey => {
            // Find all outgoing edges/connections for this page to support multi-branching (e.g. Listicles linking to multiple offers)
            const outgoingEdges = edges.filter(e => e.source === `page_${pageKey}`);
            const targetNodes = outgoingEdges.map(edge => nodes.find(n => n.id === edge.target)).filter(Boolean);
            
            // Sort target nodes by Y coordinate to maintain standard top-to-bottom layout hierarchy
            const sortedTargetNodes = [...targetNodes].sort((a, b) => (a.position?.y || 0) - (b.position?.y || 0));
            const targetNode = sortedTargetNodes[0] || null;
            
            let nextPath = null;
            let customUrl = null;

            if (targetNode) {
                if (targetNode.id.startsWith('offer')) {
                    nextPath = 'offer';
                    // Extract custom affiliate link from the offer node if present
                    customUrl = targetNode.data?.affiliateLink || null;
                } else if (targetNode.id.startsWith('capture')) {
                    nextPath = 'capture';
                } else {
                    nextPath = targetNode.id.replace('page_', '');
                }
            }
            
            let nextPageCopy = nextPath ? allPagesData[nextPath] : null;
            let captureNextUrl = '{affiliate_link}';
            if (nextPath === 'capture' && targetNode) {
                nextPageCopy = {
                    headline: targetNode.data?.config?.headline || 'Unlock Instant Access',
                    body: targetNode.data?.config?.subtext || 'Enter your best email address below to secure your spot and redirect to the presentation.',
                    cta: targetNode.data?.config?.btnText || 'Get Instant Access'
                };
                
                const captureTargetEdge = edges.find(e => e.source === targetNode.id);
                const captureTargetNode = captureTargetEdge ? nodes.find(n => n.id === captureTargetEdge.target) : null;
                if (captureTargetNode) {
                    if (captureTargetNode.id.startsWith('offer')) {
                         captureNextUrl = captureTargetNode.data?.affiliateLink || '{affiliate_link}';
                    } else if (captureTargetNode.id.startsWith('page_')) {
                         captureNextUrl = '{tracking_url}/' + captureTargetNode.id.replace('page_', '');
                    }
                }
            }
            // Re-compile this page's HTML with the updated destination link and next page copy for the capture popup modal
            let newHtml = compileHTML(allPagesData[pageKey], false, pageKey, nextPath, customUrl, nextPageCopy, captureNextUrl);
            
            // Replace multi-branch placeholders ({affiliate_link_1}, {affiliate_link_2}, etc.) for listicle pages by mapping exact connection handles
            const maxSlots = 5;
            for (let i = 1; i <= maxSlots; i++) {
                // Find edge connected specifically to this offer slot handle (e.g. source-offer-1)
                const slotEdge = outgoingEdges.find(e => e.sourceHandle === `source-offer-${i}`);
                const slotNode = slotEdge ? nodes.find(n => n.id === slotEdge.target) : null;
                
                let nodeLink = '';
                if (slotNode) {
                    if (slotNode.id.startsWith('offer')) {
                        nodeLink = slotNode.data?.affiliateLink || '{affiliate_link}';
                    } else if (slotNode.id.startsWith('page_')) {
                        nodeLink = `{tracking_url}/${slotNode.id.replace('page_', '')}`;
                    } else if (slotNode.id.startsWith('capture')) {
                        nodeLink = 'javascript:openCapturePopup()';
                    }
                } else {
                    // Fallback to visual top-to-bottom sequence if no direct handle match (e.g. legacy connections)
                    const fallbackNode = sortedTargetNodes[i - 1];
                    if (fallbackNode) {
                        if (fallbackNode.id.startsWith('offer')) {
                            nodeLink = fallbackNode.data?.affiliateLink || '{affiliate_link}';
                        } else if (fallbackNode.id.startsWith('page_')) {
                            nodeLink = `{tracking_url}/${fallbackNode.id.replace('page_', '')}`;
                        } else if (fallbackNode.id.startsWith('capture')) {
                            nodeLink = 'javascript:openCapturePopup()';
                        }
                    }
                }
                
                if (nodeLink) {
                    newHtml = newHtml.split(`{affiliate_link_${i}}`).join(nodeLink);
                }
            }

            // OTO Page custom handles replacing
            if (pageKey.toLowerCase().includes('oto') || (pageNode && (pageNode.type === 'oto' || pageNode.data?.stage === 'oto'))) {
                const yesEdge = outgoingEdges.find(e => e.sourceHandle === 'source-yes');
                const noEdge = outgoingEdges.find(e => e.sourceHandle === 'source-no');
                
                const yesNode = yesEdge ? nodes.find(n => n.id === yesEdge.target) : null;
                const noNode = noEdge ? nodes.find(n => n.id === noEdge.target) : null;
                
                let yesLink = '';
                if (yesNode) {
                    if (yesNode.id.startsWith('offer')) {
                        yesLink = yesNode.data?.affiliateLink || '{affiliate_link}';
                    } else if (yesNode.id.startsWith('page_')) {
                        yesLink = `{tracking_url}/${yesNode.id.replace('page_', '')}`;
                    } else if (yesNode.id.startsWith('capture')) {
                        yesLink = 'javascript:openCapturePopup()';
                    }
                } else {
                    yesLink = '{affiliate_link}';
                }
                
                let noLink = '';
                if (noNode) {
                    if (noNode.id.startsWith('offer')) {
                        noLink = noNode.data?.affiliateLink || '{affiliate_link}';
                    } else if (noNode.id.startsWith('page_')) {
                        noLink = `{tracking_url}/${noNode.id.replace('page_', '')}`;
                    } else if (noNode.id.startsWith('capture')) {
                        noLink = 'javascript:openCapturePopup()';
                    }
                } else {
                    noLink = nextPath ? (nextPath === 'offer' ? '{affiliate_link}' : `{tracking_url}/${nextPath}`) : '{affiliate_link}';
                }
                
                newHtml = newHtml.split('{oto_yes_link}').join(yesLink);
                newHtml = newHtml.split('{oto_no_link}').join(noLink);
            }

            newLivePages[pageKey] = newHtml;

            // Also update any page variants that match this base page key, or create if missing
            const variantExists = updatedPageVariants.some(v => v?.name?.toLowerCase() === pageKey?.toLowerCase());
            if (variantExists) {
                updatedPageVariants = updatedPageVariants.map(v => {
                    if (v?.name?.toLowerCase() === pageKey?.toLowerCase()) {
                        return { ...v, html_content: newHtml };
                    }
                    return v;
                });
            } else {
                updatedPageVariants.push({
                    id: `var_${pageKey}_${Math.floor(Math.random() * 100000)}`,
                    name: pageKey,
                    html_content: newHtml,
                    status: 'live',
                    clicks: 0,
                    conversions: 0
                });
            }
        });
        setLivePages(newLivePages);
        console.log("🔄 Re-compiled funnel pages and variants to respect new sequence.");
    }

    if (persist && campaignData.id) {
        try {
            const campaignRef = doc(db, 'campaigns', campaignData.id);
            await updateDoc(campaignRef, {
                funnelArchitecture: { nodes: sanitizedNodes, edges: sanitizedEdges },
                pages: allPagesData,
                pageVariants: updatedPageVariants,
                updatedAt: new Date().toISOString()
            });
            console.log("💾 Saved architecture, pages, and updated variant HTML to Firestore.");
        } catch (error) {
            console.error("Error saving architecture:", error);
        }
    }
    
    setCampaignData(prev => ({ 
        ...prev, 
        funnelArchitecture: { nodes: sanitizedNodes, edges: sanitizedEdges },
        pages: allPagesData,
        pageVariants: updatedPageVariants
    }));
  };

  const handleAngleStatusChange = async (campaignId, angleId, newStatus) => {
    setCampaignData(prev => {
      if (prev.id !== campaignId) return prev;
      
      const updatedAngles = (prev.anglePlans || []).map(a => {
        if (newStatus === 'active') {
          return {
            ...a,
            status: a.id === angleId ? 'active' : 'queued'
          };
        } else {
          return {
            ...a,
            status: a.id === angleId ? newStatus : a.status
          };
        }
      });

      const updated = {
        ...prev,
        anglePlans: updatedAngles
      };

      const campaignRef = doc(db, 'campaigns', campaignId);
      updateDoc(campaignRef, {
        anglePlans: updatedAngles,
        updatedAt: new Date().toISOString()
      }).then(() => {
        console.log("💾 Persisted angle status change to Firestore.");
      }).catch(err => {
        console.error("Error saving angle status change to Firestore:", err);
      });

      return updated;
    });
  };

  const handlePageBuilt = async (pageId, html) => {
    // Update livePages state
    setLivePages(prev => ({
      ...prev,
      [pageId]: html
    }));

    setCampaignData(prev => {
      const updatedPages = { ...(prev.pages || {}), [pageId]: html };
      const currentVariants = prev.pageVariants || [];
      const variantIndex = currentVariants.findIndex(v => v.name === pageId || v.id === pageId);
      
      let updatedVariants = [...currentVariants];
      if (variantIndex > -1) {
        updatedVariants[variantIndex] = {
          ...updatedVariants[variantIndex],
          html_content: html
        };
      } else {
        updatedVariants.push({
          id: `var_${pageId}_${Math.floor(Math.random() * 100000)}`,
          name: pageId,
          html_content: html,
          status: 'live',
          clicks: 0,
          conversions: 0
        });
      }

      const updated = {
        ...prev,
        pages: updatedPages,
        pageVariants: updatedVariants
      };

      if (prev.id) {
        const campaignRef = doc(db, 'campaigns', prev.id);
        updateDoc(campaignRef, {
          pages: updatedPages,
          pageVariants: updatedVariants,
          updatedAt: new Date().toISOString()
        }).then(() => {
          console.log("💾 Persisted built page HTML to Firestore.");
        }).catch(err => {
          console.error("Error saving built page to Firestore:", err);
        });
      }

      return updated;
    });
  };

  const handleRenamePage = (oldId, newId) => {
    if (oldId === newId) return;
    setLivePages(prev => {
        const next = { ...prev };
        next[newId] = next[oldId];
        delete next[oldId];
        return next;
    });
    if (activePageId === oldId) setActivePageId(newId);
  };

  const handleInitializeAgentWorkspace = (data) => {
    setIsAgentInitModalOpen(false);
    setLivePages({});
    setIsChatBubbleOpen(false);
    
    if (data && (data.affiliateLink || data.niche)) {
        // Sync all wizard form data to global campaign state immediately
        const initialCampaign = {
            ...campaignData,
            name: data?.name || campaignData?.name,
            network: data.network || campaignData.network,
            affiliateLink: data.affiliateLink || campaignData.affiliateLink,
            salesPageLink: data.salesPageLink || campaignData.salesPageLink,
            niche: data.niche || campaignData.niche,
            payout: data.payout || campaignData.payout,
            payoutModel: data.payoutModel || campaignData.payoutModel,
            gravityScore: data.gravityScore || campaignData.gravityScore,
            networkEpc: data.networkEpc || campaignData.networkEpc,
            targetGeo: data.targetGeo || campaignData.targetGeo,
            allowedTraffic: data.allowedTraffic || campaignData.allowedTraffic,
            auditData: data.auditData || null,
            selectedStrategies: data.selectedStrategies || []
        };
        
        setCampaignData(initialCampaign);
        
        if (data.auditData) {
            setAuditResults(data.auditData);
            setScrapedText(data.auditData.scrapedText || '');
        }

        setBuilderHistory([
            { role: 'assistant', content: `Hey Ninja! I've loaded the context for "${data?.name || 'your campaign'}" (${data?.network || ''}). Let me analyze the offer and start building...` }
        ]);
        setCurrentView('agent-workspace');
        
        setTimeout(() => {
            if (data.affiliateLink) {
                const trafficToUse = data.selectedTraffic || data.auditData?.recommendedTraffic || 'General';
                const funnelToUse = data.selectedFunnel || data.auditData?.recommendedFunnel || 'landing';
                autoGenerateFunnel(data.affiliateLink, trafficToUse, funnelToUse, data.network, initialCampaign, data.auditData);
            } else if (data.niche) {
                handleBuilderChat(`Let's build a funnel for this niche: ${data.niche}`);
            }
        }, 800);
    } else {
        setBuilderHistory([
            { role: 'assistant', content: 'Hey Ninja! What are we promoting today? Give me an affiliate link or just tell me the niche.' }
        ]);
        setCurrentView('agent-workspace');
    }
  };

  const handleLaunchCampaign = async () => {
    setIsLaunching(true);
    try {
      const finalData = { 
        ...campaignData, 
        username: userProfile.affiliateId || campaignData.username || 'ninja_master', 
        createdAt: new Date().toISOString()
      };
      
      const res = await fetch('/api/campaigns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(finalData)
      });
      const data = await res.json();
      
      // Update local campaign data with the generated ID to prevent duplicates if clicked again
      if (data.campaign) {
          setCampaignData(prev => ({ ...prev, id: data.campaign.id }));
      }
      
      for (const key of Object.keys(livePages)) {
          let finalHtml = livePages[key];
          if (key === 'capture' && campaignData.captureMethod === 'custom' && campaignData.customHtmlForm) {
              const trackingScript = `<script>
                  document.addEventListener('submit', function(e) {
                      if (e.target.tagName === 'FORM') {
                          fetch('/api/track/optin', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slug: '${finalData.slug}' }) }).catch(console.error);
                      }
                  });
              </script>`;
              const customFormWithTracking = `<div class="custom-autoresponder mt-8 w-full max-w-md mx-auto">${campaignData.customHtmlForm}</div>${trackingScript}`;
              finalHtml = finalHtml.replace(/<!-- OPTIN_FORM_START -->[\\s\\S]*<!-- OPTIN_FORM_END -->/, customFormWithTracking);
          }
          await fetch(`/api/campaigns/${finalData.slug}/variants`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ name: key, html_content: finalHtml, status: 'published' })
          });
      }
      fetchData();
      setIsWizardOpen(false);
      setCurrentView('dashboard');
    } catch (err) { console.error(err); }
    setIsLaunching(false);
  };

  const handleLogout = async () => {
    await signOut(auth);
    setUserProfile({ affiliateId: '', mailerXpApi: '' });
    localStorage.removeItem('ninja_profile');
    setCurrentView('landing');
  };

  const handleDeleteCampaign = async (id) => {
    if (!id) {
      console.error('No campaign ID provided for deletion');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this campaign? This action cannot be undone.')) return;
    
    try {
      console.log(`[CLIENT] Attempting to delete campaign: ${id}`);
      const response = await fetch(`/api/campaigns/${id}/delete`, { method: 'POST' });
      
      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('[CLIENT] Delete result:', result);
      
      if (result.success) {
        // Optimistic UI update: Remove from local state immediately
        setLinksData(prev => prev.filter(link => link.id !== id));
        console.log('[CLIENT] Campaign removed from local state. Fetching fresh data to sync...');
        await fetchData();
        console.log('[CLIENT] Dashboard refreshed after deletion');
      } else {
        alert('Failed to delete campaign: ' + (result.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('[CLIENT] Delete error:', err);
      alert('Error deleting campaign: ' + err.message);
    }
  };

  const handleModelCompetitor = (spyData) => {
    setIsSpyModalOpen(false);
    
    // Set a specialized starting context for the agent
    setBuilderHistory([
      { role: 'assistant', content: `**Intelligence Received!** 🕵️‍♂️\n\nI've analyzed the competitor funnel. Here is the strategy I'll use to beat them:\n\n**Hook:** ${spyData.hook}\n**Angle:** ${spyData.angle}\n**Strategy:** ${spyData.ninjaImprovement}\n\nWhat is the affiliate link you want me to use for this campaign?` }
    ]);
    
    setCurrentView('agent-workspace');
    
    // Open the chat bubble if it was closed
    setIsChatBubbleOpen(true);
  };

  const [renderError, setRenderError] = useState(null);

  useEffect(() => {
    const handleError = (e) => {
      console.error("[CRITICAL ERROR]", e);
      setRenderError(e.message || "Unknown error");
    };
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  if (renderError) {
    return <div style={{background: 'red', color: 'white', padding: '20px', fontSize: '20px', zIndex: 99999, position: 'fixed', inset: 0}}>
      <h1>CRITICAL APP ERROR</h1>
      <pre>{renderError}</pre>
    </div>;
  }

  if (customDomainStatus) {
    return (
      <div className="bg-slate-950 text-slate-300 min-h-screen flex items-center justify-center p-6 font-sans">
        <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-3xl p-8 text-center shadow-2xl space-y-4">
          <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center mx-auto text-3xl">
            🌐
          </div>
          <h1 className="text-xl font-black text-white">{customDomainStatus.hostname}</h1>
          <p className="text-xs text-amber-400 font-bold uppercase tracking-wider">
            {customDomainStatus.status === 404 ? '404 — Not Found' : 'Domain Not Active'}
          </p>
          <p className="text-xs text-slate-400 leading-relaxed">
            {customDomainStatus.error || 'This custom domain is connected to AffiliateFlow, but no active funnel is configured for this URL.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <FunnelAnalyticsModal campaign={analyzingCampaign} isOpen={!!analyzingCampaign} onClose={() => setAnalyzingCampaign(null)} />
      <ProfileModal profile={userProfile} isOpen={isProfileModalOpen} onClose={() => setIsProfileModalOpen(false)} onSave={handleUpdateProfile} />
      <AgentInitModal isOpen={isAgentInitModalOpen} onClose={() => setIsAgentInitModalOpen(false)} onInitialize={handleInitializeAgentWorkspace} />
      <CompetitionSpyModal isOpen={isSpyModalOpen} onClose={() => setIsSpyModalOpen(false)} onModel={handleModelCompetitor} />
      <OfferScoutModal isOpen={isScoutModalOpen} onClose={() => setIsScoutModalOpen(false)} />
      {isWizardOpen && (
        <CampaignWizard
          isOpen={isWizardOpen}
          onClose={() => setIsWizardOpen(false)}
          campaignData={campaignData}
          setCampaignData={setCampaignData}
          userProfile={userProfile}
          onLaunchSuccess={(savedCampaign) => {
            setCampaignData(savedCampaign);
            setCurrentView('agent-workspace');
            setIsWizardOpen(false);
          }}
        />
      )}
      

      
      {currentView === 'landing' ? (
        <LandingPage onLogin={() => setCurrentView('dashboard')} />
      ) : currentView === 'partner-hub' ? (
        <div className="min-h-screen bg-slate-950" dangerouslySetInnerHTML={{ __html: partnerHubHtml }} />
      ) : currentView === 'campaign-builder' ? (
        <CampaignBuilder />
      ) : (
        <div className="h-screen flex flex-col bg-[#050507] text-slate-300 font-sans overflow-hidden">
          {/* Top Navbar Workspace Layout */}
          {currentView !== 'agent-workspace' && currentView !== 'campaign-builder-new' && (
            <header className="h-20 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-8 shrink-0 z-[100]">
              {/* Branding */}
              <div className="flex items-center">
                <div className="p-2 bg-gradient-to-br from-[#33D5E6] via-[#02B6FF] to-[#0A22EE] rounded-xl mr-3 shadow-lg shadow-[#02B6FF]/25">
                  <Rocket className="text-white size-5" />
                </div>
                <h1 className="text-xl font-black text-white tracking-tight mr-12">Affiliate Flow AI</h1>
                
                {/* Navigation Tabs */}
                <nav className="flex items-center space-x-1">
                  <button onClick={() => setCurrentView('dashboard')} className={`flex items-center px-5 py-2.5 rounded-xl transition-all font-bold text-sm ${currentView === 'dashboard' ? 'bg-[#02B6FF]/15 text-[#33D5E6] border border-[#02B6FF]/25 shadow-[0_0_15px_rgba(2,182,255,0.05)]' : 'hover:bg-slate-800 text-slate-400'}`}>
                    <TrendingUp className="mr-2.5 size-4" /> Dashboard
                  </button>
                  <button onClick={() => setCurrentView('partner-hub-manager')} className={`flex items-center px-5 py-2.5 rounded-xl transition-all font-bold text-sm ${currentView === 'partner-hub-manager' ? 'bg-[#02B6FF]/15 text-[#33D5E6] border border-[#02B6FF]/25 shadow-[0_0_15px_rgba(2,182,255,0.05)]' : 'hover:bg-slate-800 text-slate-400'}`}>
                    <Star className="mr-2.5 size-4" /> Partner Hub
                  </button>

                  <button 
                    onClick={() => setIsSpyModalOpen(true)} 
                    className="flex items-center px-5 py-2.5 rounded-xl transition-all font-bold text-sm hover:bg-[#02B6FF]/15 hover:text-[#33D5E6] hover:border-[#02B6FF]/25 text-slate-400 border border-transparent"
                  >
                    <Search className="mr-2.5 size-4" /> Spy Competition
                  </button>
                  <button
                    onClick={() => setCurrentView('offer-scout')}
                    className={`flex items-center px-5 py-2.5 rounded-xl transition-all font-bold text-sm ${currentView === 'offer-scout' ? 'bg-[#0A22EE]/15 text-[#33D5E6] border border-[#0A22EE]/25 shadow-[0_0_15px_rgba(10,34,238,0.05)]' : 'hover:bg-slate-800 text-slate-400'}`}
                  >
                    <Compass className="mr-2.5 size-4" /> Offer Scout
                  </button>
                  <button
                    onClick={() => setCurrentView('paid-traffic')}
                    className={`flex items-center px-5 py-2.5 rounded-xl transition-all font-bold text-sm ${currentView === 'paid-traffic' ? 'bg-[#02B6FF]/15 text-[#33D5E6] border border-[#02B6FF]/25 shadow-[0_0_15px_rgba(2,182,255,0.05)]' : 'hover:bg-slate-800 text-slate-400'}`}
                  >
                    <DollarSign className="mr-2.5 size-4" /> Paid Traffic &amp; Reports
                  </button>
                  <button
                    onClick={() => {
                      setCurrentView('settings');
                      window.history.pushState(null, '', '/settings');
                    }}
                    className={`flex items-center px-5 py-2.5 rounded-xl transition-all font-bold text-sm ${currentView === 'settings' ? 'bg-[#02B6FF]/15 text-[#33D5E6] border border-[#02B6FF]/25 shadow-[0_0_15px_rgba(2,182,255,0.05)]' : 'hover:bg-slate-800 text-slate-400'}`}
                  >
                    <SettingsIcon className="mr-2.5 size-4" /> Settings
                  </button>
                </nav>
              </div>

              {/* Profile Dropdown Area */}
              <div className="relative" id="user-dropdown-container">
                <button 
                  onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                  className="flex items-center space-x-3 p-1.5 pl-3 rounded-2xl hover:bg-slate-800 border border-transparent hover:border-slate-700 transition-all focus:outline-none"
                >
                  <div className="text-right hidden sm:block">
                    <div className="text-xs font-black text-white">{userProfile.affiliateId || 'Ninja'}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-widest leading-none mt-0.5">Active Stealth Profile</div>
                  </div>
                  <div className="size-10 bg-gradient-to-br from-[#33D5E6] via-[#02B6FF] to-[#0A22EE] rounded-xl flex items-center justify-center shadow-lg transform group-hover:rotate-6 transition-transform">
                    <User className="text-white size-5" />
                  </div>
                </button>

                {/* User Dropdown Menu */}
                {isUserDropdownOpen && (
                  <div className="absolute right-0 mt-3 w-64 bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden py-2 animate-in fade-in zoom-in-95 duration-200">
                    <div className="px-4 py-3 border-b border-slate-800 mb-2">
                      <p className="text-xs text-slate-500 font-bold uppercase tracking-widest mb-1">Authenticated Account</p>
                      <p className="text-sm font-black text-white truncate">{auth.currentUser?.email}</p>
                    </div>
                    <button 
                      onClick={() => {
                        setCurrentView('settings');
                        window.history.pushState(null, '', '/settings');
                        setIsUserDropdownOpen(false);
                      }}
                      className="w-full flex items-center px-4 py-3 text-sm text-slate-300 hover:bg-slate-800 transition-colors"
                    >
                      <SettingsIcon className="mr-3 size-4 text-[#33D5E6]" /> Settings
                    </button>
                    <button 
                      onClick={() => { setIsProfileModalOpen(true); setIsUserDropdownOpen(false); }}
                      className="w-full flex items-center px-4 py-3 text-sm text-slate-300 hover:bg-slate-800 transition-colors"
                    >
                      <Edit2 className="mr-3 size-4 text-indigo-400" /> Global Profile
                    </button>
                    <button 
                      onClick={() => { handleLogout(); setIsUserDropdownOpen(false); }}
                      className="w-full flex items-center px-4 py-3 text-sm text-rose-400 hover:bg-rose-500/10 transition-colors border-t border-slate-800"
                    >
                      <LogOut className="mr-3 size-4" /> Sign Out
                    </button>
                  </div>
                )}
              </div>
            </header>
          )}

          <div className="flex-1 flex overflow-hidden">
             <main className="flex-1 overflow-hidden bg-slate-950 relative flex flex-col">
                {currentView === 'dashboard' ? (
                  <div className="flex-1 overflow-y-auto custom-scrollbar">
                    <div className="p-8 max-w-7xl mx-auto space-y-10 animate-in fade-in duration-500">
                    
                    </div>

                    <div className="p-8 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                           <div>
                              <h2 className="text-3xl font-black text-white tracking-tight mb-2">Active Campaigns</h2>
                              <p className="text-slate-500">You have {linksData.length} campaigns currently generating traffic.</p>
                           </div>
                           <button 
                              onClick={fetchData} 
                              className="p-3 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-indigo-400 rounded-xl transition-all shadow-lg active:scale-95 border border-slate-700/50"
                              title="Refresh Data"
                           >
                              <RefreshCw size={20} className={linksData.length === 0 ? 'animate-spin' : ''} />
                           </button>
                        </div>
                       <div className="flex items-center gap-3">
                          <button 
                              onClick={() => setCurrentView('campaign-builder-new')}
                              className="flex items-center px-6 py-4 bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-white border border-slate-800 hover:border-slate-700 rounded-2xl transition-all font-black text-sm active:scale-95 shadow-sm"
                              title="Start with a blank canvas to design a custom funnel flow"
                           >
                              <LayoutTemplate className="mr-2 size-4" /> Blank Canvas
                           </button>
                          <button 
                              onClick={() => openWizard(null)}
                              className="flex items-center px-8 py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-2xl shadow-xl shadow-indigo-600/20 transition-all font-black text-sm group active:scale-95"
                              title="Create a fully optimized affiliate funnel step-by-step"
                           >
                              <Plus className="mr-2 size-5 group-hover:rotate-90 transition-transform" /> Setup Campaign
                           </button>
                       </div>
                     </div>

                     <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="bg-slate-900 border border-slate-800 p-6 rounded-[2rem] shadow-sm relative overflow-hidden group">
                           <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 rounded-full -mr-16 -mt-16 transition-transform group-hover:scale-110"></div>
                           <div className="flex items-center text-slate-500 text-xs font-black uppercase tracking-widest mb-4"><MousePointerClick className="mr-2 text-indigo-400" size={14}/> Total Clicks</div>
                           <div className="text-4xl font-black text-white">{stats.clicks || 0}</div>
                        </div>
                        <div className="bg-slate-900 border border-slate-800 p-6 rounded-[2rem] shadow-sm relative overflow-hidden group">
                           <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/5 rounded-full -mr-16 -mt-16"></div>
                           <div className="flex items-center text-slate-500 text-xs font-black uppercase tracking-widest mb-4"><Users className="mr-2 text-purple-400" size={14}/> Total Optins</div>
                           <div className="text-4xl font-black text-white">{stats.optins || 0}</div>
                        </div>
                        <div className="bg-slate-900 border border-slate-800 p-6 rounded-[2rem] shadow-sm relative overflow-hidden group">
                           <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full -mr-16 -mt-16"></div>
                           <div className="flex items-center text-slate-500 text-xs font-black uppercase tracking-widest mb-4"><Target className="mr-2 text-emerald-400" size={14}/> Sales</div>
                           <div className="text-4xl font-black text-white">{stats.conversions || 0}</div>
                        </div>
                        <div className="bg-slate-900 border border-slate-800 p-6 rounded-[2rem] shadow-sm relative overflow-hidden group">
                           <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full -mr-16 -mt-16"></div>
                           <div className="flex items-center text-slate-500 text-xs font-black uppercase tracking-widest mb-4"><DollarSign className="mr-2 text-amber-400" size={14}/> Net ROI</div>
                           <div className="text-4xl font-black text-white text-emerald-400">+{stats.roi || '0%'}</div>
                        </div>
                     </div>

                    <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] overflow-hidden shadow-2xl">
                       <div className="overflow-x-auto">
                          <table className="w-full text-left">
                             <thead>
                                <tr className="border-b border-slate-800/50">
                                   <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest">Campaign / Niche</th>
                                   <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest text-center">Performance Matrix</th>
                                   <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest text-center">Financial Matrix</th>
                                   <th className="px-8 py-6 text-[10px] font-black text-slate-500 uppercase tracking-widest text-right">Actions</th>
                                </tr>
                             </thead>
                             <tbody className="divide-y divide-slate-800/30">
                                {Array.isArray(linksData) && [...linksData].sort((a, b) => {
                                    if (a.isPartnerHub && !b.isPartnerHub) return -1;
                                    if (!a.isPartnerHub && b.isPartnerHub) return 1;
                                    return 0;
                                }).map((link) => {
                                   const clicks = link.clicks || 0;
                                   const conversions = link.conversions || 0;
                                   const revenue = link.revenue || 0;
                                   
                                   // Extract CPC from traffic nodes in funnelArchitecture
                                   let cpc = 0.18;
                                   if (link.funnelArchitecture?.nodes) {
                                     const trafficNode = link.funnelArchitecture.nodes.find(n => n.type === 'traffic' || n.type === 'traffic-source');
                                     if (trafficNode) {
                                       if (trafficNode.pageContent?.cpc !== undefined) {
                                         cpc = parseFloat(trafficNode.pageContent.cpc);
                                       } else if (trafficNode.data?.config?.cpc !== undefined) {
                                         cpc = parseFloat(trafficNode.data.config.cpc);
                                       }
                                     }
                                   }
                                   
                                   const spend = clicks * cpc;
                                   const epc = clicks > 0 ? revenue / clicks : 0;
                                   const netProfit = revenue - spend;
                                   const roiVal = spend > 0 ? (netProfit / spend) * 100 : 0;
                                   const isExpanded = !!expandedCampaigns[link.id];

                                   return (
                                      <React.Fragment key={link.id}>
                                        <tr className={`group hover:bg-slate-800/40 transition-colors ${isExpanded ? 'bg-slate-800/30' : ''}`}>
                                         <td className="px-8 py-6">
                                            <div className="flex items-center gap-3">
                                               <button 
                                                  onClick={() => toggleCampaignExpanded(link.id)}
                                                  className={`p-1.5 rounded-lg border transition-all ${isExpanded ? 'bg-indigo-600 border-indigo-500 text-white' : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-white hover:border-slate-600'}`}
                                                  title={isExpanded ? "Collapse Angles" : "Expand Angles"}
                                               >
                                                  <ChevronDown size={14} className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                                               </button>
                                               <div>
                                                  <div className="font-bold text-white mb-1 flex items-center gap-2">
                                                     <span>{link?.name || ''}</span>
                                                     {((link.angles || link.anglePlans)?.length > 0) && (
                                                        <span className="text-[9px] px-2 py-0.5 rounded-md bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 font-mono">
                                                           {(link.angles || link.anglePlans).length} {((link.angles || link.anglePlans).length === 1) ? 'Angle' : 'Angles'}
                                                        </span>
                                                     )}
                                                  </div>
                                                  <div className="text-xs text-slate-500 font-mono flex items-center gap-2">
                                                     {link.customDomain && link.customDomain !== 'xpgo.link' ? (
                                                        <span className="text-[10px] px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-400 border border-indigo-500/25 font-bold flex items-center gap-1" title="Branded Custom Domain">
                                                           <span>🌐</span> {link.customDomain}
                                                        </span>
                                                     ) : (
                                                        <span>/u/{link.username}/{link.slug}</span>
                                                     )}
                                                  </div>
                                               </div>
                                            </div>
                                         </td>
                                         <td className="px-8 py-6">
                                            <div className="flex items-center justify-center space-x-6">
                                               <div className="text-center">
                                                  <div className="text-xs text-slate-500 uppercase mb-1">Clicks</div>
                                                  <div className="font-black text-white">{clicks}</div>
                                               </div>
                                               <div className="text-center">
                                                  <div className="text-xs text-slate-500 uppercase mb-1">Optins</div>
                                                  <div className="font-black text-purple-400">{link.optins || 0}</div>
                                               </div>
                                               <div className="text-center">
                                                  <div className="text-xs text-slate-500 uppercase mb-1">Sales</div>
                                                  <div className="font-black text-emerald-400">{conversions}</div>
                                               </div>
                                             </div>
                                          </td>
                                          <td className="px-8 py-6">
                                             <div className="flex items-center justify-center space-x-6">
                                                <div className="text-center">
                                                   <div className="text-xs text-slate-500 uppercase mb-1">Spend</div>
                                                   <div className="font-black text-white">${spend.toFixed(2)}</div>
                                                </div>
                                                <div className="text-center">
                                                   <div className="text-xs text-slate-500 uppercase mb-1">Revenue</div>
                                                   <div className="font-black text-emerald-400">${revenue.toFixed(2)}</div>
                                                </div>
                                                <div className="text-center">
                                                   <div className="text-xs text-slate-500 uppercase mb-1">EPC</div>
                                                   <div className="font-black text-amber-400">${epc.toFixed(2)}</div>
                                                </div>
                                                <div className="text-center">
                                                   <div className="text-xs text-slate-500 uppercase mb-1">ROI</div>
                                                   <div className={`font-black ${roiVal >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                                      {roiVal >= 0 ? '+' : ''}{roiVal.toFixed(1)}%
                                                   </div>
                                                </div>
                                             </div>
                                          </td>
                                          <td className="px-8 py-6 text-right">
                                             <div className="flex items-center justify-end space-x-3 transition-opacity">
                                                <button onClick={() => handleDeleteCampaign(link.id)} className="p-2.5 bg-slate-800 hover:bg-rose-500 text-white rounded-xl transition-all shadow-lg active:scale-90" title="Delete"><Trash2 size={16}/></button>
                                                <button onClick={() => handleEditCampaign(link)} className="p-2.5 bg-slate-800 hover:bg-indigo-600 text-white rounded-xl transition-all shadow-lg active:scale-90" title="Edit"><Edit2 size={16}/></button>
                                                <a href={link.customDomain && link.customDomain !== 'xpgo.link' ? `https://${link.customDomain}/` : `https://xpgo.link/u/${link.username}/${link.slug}`} target="_blank" className="p-2.5 bg-slate-800 hover:bg-slate-700 text-white rounded-xl transition-all active:scale-90" title={link.customDomain && link.customDomain !== 'xpgo.link' ? `Preview on https://${link.customDomain}/` : 'Preview'}><Globe size={16}/></a>
                                             </div>
                                          </td>
                                       </tr>
                                       {isExpanded && (
                                          <tr className="bg-slate-950/60 border-b border-slate-800/60">
                                             <td colSpan="4" className="p-6">
                                                <div className="bg-slate-900/90 border border-slate-800 rounded-3xl p-6 shadow-inner space-y-4">
                                                   <div className="flex items-center justify-between border-b border-slate-800/60 pb-3">
                                                      <div className="flex items-center gap-2">
                                                         <span className="text-xs font-black uppercase tracking-widest text-indigo-400">Angle Split-Test Comparison</span>
                                                         <span className="text-[10px] text-slate-500 font-mono">({link.name || link.slug})</span>
                                                      </div>
                                                      <span className="text-[10px] text-slate-500 font-mono">Two-proportion z-test statistical confidence</span>
                                                   </div>
                                                   <AngleComparisonTable 
                                                      campaign={link} 
                                                      campaignId={link.id} 
                                                      breakEvenCpc={link.networkEpc || link.offerEpc || 0}
                                                      onAngleUpdated={(idx, updatedAngle) => {
                                                         fetchData();
                                                      }}
                                                   />
                                                </div>
                                             </td>
                                          </tr>
                                       )}
                                     </React.Fragment>
                                   );
                                })}
                             </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : currentView === 'partner-hub-manager' ? (
                  (() => {
                    const partnerHubCampaign = Array.isArray(linksData) ? linksData.find(c => c.isPartnerHub) : null;
                    return (
                      <div className="flex-1 overflow-y-auto p-8 bg-slate-950 text-slate-300">
                        <div className="max-w-4xl mx-auto space-y-8">
                          {/* Top Branding / Header */}
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-3">
                                <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-2xl text-purple-400">
                                  <Star className="size-6 animate-pulse" />
                                </div>
                                <div>
                                  <h2 className="text-3xl font-black text-white tracking-tight">Partner Hub Manager</h2>
                                  <p className="text-slate-400 text-sm mt-1">Configure and deploy your official Affiliate Flow AI promotional funnel</p>
                                </div>
                              </div>
                            </div>
                            {partnerHubCampaign && (
                              <span className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-xl text-xs font-black uppercase tracking-widest">
                                ● Active
                              </span>
                            )}
                          </div>

                          {/* Main Settings Panel */}
                          <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-8 md:p-10 shadow-2xl relative overflow-hidden space-y-8">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full -mr-32 -mt-32 blur-3xl pointer-events-none"></div>
                            <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/5 rounded-full -ml-32 -mb-32 blur-3xl pointer-events-none"></div>

                            <div className="space-y-6">
                              <h3 className="text-lg font-black text-white uppercase tracking-widest flex items-center gap-2">
                                <Zap className="text-indigo-400 size-5" /> Campaign Configuration
                              </h3>
                              <p className="text-slate-400 text-sm leading-relaxed">
                                The Partner Hub campaign is your flagship promotional engine. Hosted on the primary domain and locked to the official Affiliate Flow AI offer, it ensures maximum tracking integrity and credited commission conversions.
                              </p>
                            </div>

                            {/* Input Form */}
                            <div className="space-y-4 max-w-2xl">
                              <label className="block text-xs font-black text-slate-400 uppercase tracking-widest">
                                JVZoo Affiliate Link
                              </label>
                              <div className="flex flex-col md:flex-row gap-3">
                                <div className="relative flex-1">
                                  <LinkIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 size-5" />
                                  <input 
                                    type="text"
                                    value={userJVZooLink}
                                    onChange={(e) => setUserJVZooLink(e.target.value)}
                                    placeholder="https://jvzoo.com/c/..."
                                    className="w-full pl-12 pr-4 py-4 bg-slate-950 border border-slate-800 focus:border-indigo-500 rounded-2xl text-white focus:outline-none transition-all font-mono text-sm placeholder-slate-600"
                                  />
                                </div>
                                <button 
                                  onClick={() => handleGenerateRecommendationFunnel(false)}
                                  disabled={isLaunching}
                                  className="px-8 py-4 bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-600/50 text-white rounded-2xl shadow-xl shadow-indigo-600/20 transition-all font-black text-sm flex items-center justify-center gap-2 whitespace-nowrap shrink-0 group active:scale-95"
                                >
                                  {isLaunching ? (
                                    <>
                                      <Loader2 className="animate-spin size-4 animate-spin-slow" /> Deploying...
                                    </>
                                  ) : (
                                    <>
                                      <Rocket className="size-4 group-hover:translate-x-0.5 transition-transform" /> Save & Deploy Funnel
                                    </>
                                  )}
                                </button>
                              </div>
                              <p className="text-[10px] text-slate-500 uppercase tracking-widest font-black">
                                Enter your JVZoo partner link to instantly direct traffic to your credited checkout page.
                              </p>
                            </div>

                            {/* Live Link Section */}
                            {partnerHubCampaign && (
                              <div className="p-6 bg-slate-950/50 border border-slate-800 rounded-2xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                                <div className="space-y-1">
                                  <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Live Funnel Link</span>
                                  <div className="text-white font-mono font-bold text-sm tracking-tight select-all">
                                    {`affiliateflow.ai/u/${partnerHubCampaign.username}/${partnerHubCampaign.slug}`}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 w-full md:w-auto">
                                  <button 
                                    onClick={() => handleCopyLink(`https://affiliateflow.ai/u/${partnerHubCampaign.username}/${partnerHubCampaign.slug}`, 'Partner Link')}
                                    className="flex-1 md:flex-none flex items-center justify-center gap-2 px-5 py-2.5 bg-slate-800 hover:bg-slate-700/80 text-slate-300 rounded-xl text-xs font-bold transition-all active:scale-95 border border-slate-700/50"
                                  >
                                    <Copy className="size-3.5" /> Copy Link
                                  </button>
                                  <a 
                                    href={`https://affiliateflow.ai/u/${partnerHubCampaign.username}/${partnerHubCampaign.slug}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 md:flex-none flex items-center justify-center gap-2 px-5 py-2.5 bg-indigo-600/10 hover:bg-indigo-600/20 text-indigo-400 rounded-xl text-xs font-bold transition-all border border-indigo-500/20"
                                  >
                                    <ExternalLink className="size-3.5" /> View Live
                                  </a>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Canvas & Advanced Actions */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Open Canvas Card */}
                            <div className="bg-slate-900 border border-slate-800 p-8 rounded-[2.5rem] shadow-sm relative overflow-hidden group flex flex-col justify-between h-56">
                              <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/5 rounded-full -mr-16 -mt-16 transition-transform group-hover:scale-110"></div>
                              <div>
                                <h4 className="text-sm font-black text-white uppercase tracking-widest mb-2 flex items-center gap-2">
                                  🎨 Funnel Designer
                                </h4>
                                <p className="text-slate-400 text-xs leading-relaxed">
                                  Open the visual drag-and-drop canvas map to edit page content, customize Headlines, VSL settings, and configure the Capture Popup.
                                </p>
                              </div>
                              <div>
                                {partnerHubCampaign ? (
                                  <button 
                                    onClick={() => handleEditCampaign(partnerHubCampaign)}
                                    className="flex items-center justify-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-xl shadow-lg shadow-purple-600/20 transition-all font-black text-xs group active:scale-95 w-full md:w-auto"
                                  >
                                    <Zap className="size-3.5" /> Open Partner Funnel Canvas
                                  </button>
                                ) : (
                                  <span className="text-xs text-slate-500 font-bold uppercase tracking-widest">
                                    Save your JVZoo link first to enable designer
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Reset / Maintenance Card */}
                            <div className="bg-slate-900 border border-slate-800 p-8 rounded-[2.5rem] shadow-sm relative overflow-hidden group flex flex-col justify-between h-56">
                              <div className="absolute top-0 right-0 w-32 h-32 bg-rose-500/5 rounded-full -mr-16 -mt-16 transition-transform group-hover:scale-110"></div>
                              <div>
                                <h4 className="text-sm font-black text-white uppercase tracking-widest mb-2 flex items-center gap-2">
                                  ⚙️ Funnel Reset
                                </h4>
                                <p className="text-slate-400 text-xs leading-relaxed">
                                  Restore the official promotional funnel structure back to factory defaults. This will reset custom modifications but preserve your affiliate link.
                                </p>
                              </div>
                              <div>
                                {partnerHubCampaign ? (
                                  <button 
                                    onClick={() => {
                                      if (window.confirm("Are you sure you want to restore the Partner Hub funnel to default layout? This will discard custom page edits!")) {
                                        handleGenerateRecommendationFunnel(true);
                                      }
                                    }}
                                    disabled={isLaunching}
                                    className="flex items-center justify-center gap-2 px-6 py-3 bg-slate-800 hover:bg-rose-500/10 text-rose-400 hover:text-rose-400 border border-slate-700/50 hover:border-rose-500/20 rounded-xl transition-all font-black text-xs active:scale-95 w-full md:w-auto"
                                  >
                                    <RefreshCw className="size-3.5" /> Reset to Default Layout
                                  </button>
                                ) : (
                                  <span className="text-xs text-slate-500 font-bold uppercase tracking-widest">
                                    Save your JVZoo link first to enable reset
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()
                ) : currentView === 'paid-traffic' ? (
                  <div className="flex-1 overflow-y-auto custom-scrollbar p-8 max-w-7xl mx-auto w-full">
                    <PaidTrafficManager
                      campaigns={linksData}
                      onCampaignUpdated={fetchData}
                    />
                  </div>
                ) : currentView === 'settings' ? (
                  <SettingsPage
                    onBack={() => {
                      setCurrentView('dashboard');
                      window.history.pushState(null, '', '/dashboard');
                    }}
                    onUsernameUpdated={(newUsername) => {
                      setUserProfile(prev => ({ ...prev, affiliateId: newUsername }));
                    }}
                  />
                ) : currentView === 'offer-scout' ? (
                  <OfferScout
                    openWizard={openWizard}
                    onBack={() => setCurrentView('dashboard')}
                  />
                ) : currentView === 'campaign-builder-new' ? (
                  <CampaignBuilder
                    mode="new"
                    userProfile={userProfile}
                    onBack={() => {
                      fetchData();
                      setCurrentView('dashboard');
                    }}
                    onSaveNew={(savedCampaign) => {
                      fetchData();
                      setCurrentView('dashboard');
                    }}
                  />
                ) : currentView === 'agent-workspace' ? (
                  <CampaignBuilder
                    campaignData={campaignData}
                    userProfile={userProfile}
                    onBack={() => {
                      fetchData();
                      setCurrentView('dashboard');
                    }}
                    onSaveNew={() => {
                      fetchData();
                      setCurrentView('dashboard');
                    }}
                  />
                ) : currentView === 'agent-workspace-legacy' ? (
                 <div className="flex-1 flex h-[calc(100vh-5rem)]">
                   {/* Left Panel: Chat Thread */}
                   <div className="w-[450px] bg-slate-900 border-r border-slate-800 flex flex-col shadow-2xl z-10">
                     <div className="h-20 border-b border-slate-800 flex items-center justify-between px-6 bg-indigo-600/5 shrink-0">
                       <div className="flex items-center">
                         <div className="size-10 bg-indigo-600 rounded-xl flex items-center justify-center mr-4 shadow-lg shadow-indigo-600/20 relative">
                           <Bot size={20} className="text-white" />
                           <div className="absolute -top-1 -right-1 size-3 bg-emerald-400 border-2 border-slate-900 rounded-full z-10 animate-pulse"></div>
                         </div>
                         <div>
                           <h3 className="text-sm font-black text-white uppercase tracking-widest">Campaign Builder</h3>
                           <div className="flex items-center text-[10px] text-indigo-400 font-bold uppercase tracking-widest mt-1">Context Sync: <span className="text-emerald-400 ml-1">Live</span></div>
                         </div>
                       </div>
                       <button onClick={() => setCurrentView('dashboard')} className="p-2 text-slate-500 hover:text-white bg-slate-800/50 hover:bg-slate-800 rounded-xl transition-all"><X size={16}/></button>
                     </div>
               
                     <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar bg-slate-950/50">
                       {builderHistory.map((msg, i) => (
                         <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                           {msg.role !== 'user' && (
                             <div className="size-8 bg-indigo-900 border border-indigo-500/30 rounded-full flex items-center justify-center mr-3 shrink-0 mt-1 shadow-sm"><Bot size={14} className="text-indigo-400" /></div>
                           )}
                           <div className={`max-w-[85%] px-5 py-4 text-sm rounded-2xl leading-relaxed shadow-sm ${msg.role === 'user' ? 'bg-indigo-600 text-white rounded-tr-none' : 'bg-slate-800 text-slate-200 border border-slate-700/50 rounded-tl-none'}`}>
                             {msg.content}
                           </div>
                         </div>
                       ))}
                       {isChatting && (
                          <div className="flex justify-start">
                            <div className="size-8 bg-indigo-900 border border-indigo-500/30 rounded-full flex items-center justify-center mr-3 shrink-0 mt-1 shadow-sm"><Bot size={14} className="text-indigo-400" /></div>
                            <div className="bg-slate-800 px-5 py-4 rounded-2xl flex space-x-1.5 border border-slate-700/50 items-center"><div className="size-1.5 bg-indigo-400 rounded-full animate-bounce"></div><div className="size-1.5 bg-indigo-400 rounded-full animate-bounce delay-75"></div><div className="size-1.5 bg-indigo-400 rounded-full animate-bounce delay-150"></div></div>
                          </div>
                       )}
                     </div>
               
                     <div className="p-6 border-t border-slate-800 bg-slate-900 shrink-0">
                       <div className="relative">
                         <textarea 
                           value={chatPrompt} onChange={e => setChatPrompt(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleBuilderChat())}
                           placeholder="Type a link or command..."
                           className="w-full bg-slate-950 border border-slate-800 rounded-2xl pl-5 pr-14 py-4 text-sm text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 h-28 resize-none shadow-inner custom-scrollbar"
                         />
                         <button onClick={handleBuilderChat} className="absolute bottom-4 right-4 p-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-500 transition-all shadow-lg active:scale-95"><ArrowRight size={20}/></button>
                       </div>
                     </div>
                   </div>
               
                   {/* Right Panel: Canvas */}
                   <div className="flex-1 bg-slate-950 flex flex-col overflow-hidden relative">
                     {Object.keys(livePages || {}).length > 0 ? (
                       <div className="flex-1 flex flex-col p-6">
                            <div className="flex justify-between items-start mb-6">
                               <div className="flex flex-col space-y-3">
                                  {/* Top Level Navigation */}
                                  <div className="flex space-x-2 bg-slate-900 p-1 rounded-xl border border-slate-800 self-start">
                                      <button 
                                        onClick={() => setActivePageId('funnel_map')} 
                                        className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all flex items-center ${activePageId === 'funnel_map' ? 'bg-amber-600 text-white shadow-md' : 'text-slate-500 hover:text-white'}`}
                                      >
                                          <Zap size={12} className="mr-1.5" /> Funnel Map
                                      </button>
                                      <button 
                                        onClick={() => setActivePageId(Object.keys(livePages)[0] || 'main')} 
                                        className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all flex items-center ${(!['funnel_map', 'ad_studio'].includes(activePageId)) ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:text-white'}`}
                                      >
                                          <Layout size={12} className="mr-1.5" /> Pages
                                      </button>
                                      {activePageId === 'ad_studio' && (
                                        <button 
                                          onClick={() => setActivePageId('ad_studio')} 
                                          className="px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all flex items-center bg-blue-600 text-white shadow-md"
                                        >
                                          <Megaphone size={12} className="mr-1.5" /> Ad Studio
                                        </button>
                                      )}
                                  </div>

                                  {/* Sub-Navigation for Pages */}
                                  {(!['funnel_map', 'ad_studio'].includes(activePageId)) && (
                                    <div className="flex space-x-2 animate-in slide-in-from-top-2 duration-300">
                                      {Object.keys(livePages || {}).map(pageId => (
                                        <button 
                                          key={pageId} 
                                          onClick={() => setActivePageId(pageId)} 
                                          className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all border ${activePageId === pageId ? 'bg-indigo-600/10 border-indigo-500/50 text-indigo-400' : 'bg-slate-900 border-slate-800 text-slate-500 hover:text-slate-300'}`}
                                        >
                                          {pageId}
                                        </button>
                                      ))}
                                      {activePageId.startsWith('capture') && (
                                        <button 
                                          className="px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all border bg-purple-600/10 border-purple-500/50 text-purple-400 animate-in fade-in"
                                        >
                                          📧 Capture Popup Settings
                                        </button>
                                      )}
                                    </div>
                                  )}
                               </div>

                               <div className="flex space-x-2 pt-1">
                                 <button 
                                   onClick={() => {
                                       if (window.confirm('This will completely rebuild the funnel using a fresh AI perspective. Continue?')) {
                                           autoGenerateFunnel(campaignData.affiliateLink || campaignData.niche, selectedTrafficSource, campaignData.funnelType, campaignData.network);
                                       }
                                   }}
                                   className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-[10px] font-black uppercase tracking-widest rounded-xl border border-slate-700 transition-all flex items-center"
                                 >
                                   <RefreshCw size={12} className="mr-2" /> Neural Regenerate
                                 </button>
                                 <button onClick={() => setIsWizardOpen(true)} className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-black uppercase tracking-widest rounded-xl shadow-lg shadow-emerald-500/20 flex items-center transition-all">
                                   DEPLOY LIVE
                                 </button>
                               </div>
                             </div>
                            <div className={`flex-1 rounded-3xl overflow-hidden shadow-2xl border border-slate-800 ${['ad_studio', 'funnel_map'].includes(activePageId) ? 'bg-slate-900' : 'bg-white'}`}>
                               {activePageId === 'funnel_map' ? (
                                 <FunnelVisualizer 
                                   trafficSource={selectedTrafficSource} 
                                   pages={Object.keys(livePages || {})} 
                                   network={campaignData.network} 
                                   onEditPage={handleEditPage}
                                   onDuplicatePage={handleDuplicatePage}
                                   onAddPage={handleAddPage}
                                   onRenamePage={handleRenamePage}
                                   savedArchitecture={campaignData.funnelArchitecture}
                                   onSaveArchitecture={handleSaveArchitecture}
                                   campaignId={campaignData.id}
                                   slug={campaignData.slug}
                                   username={campaignData.username}
                                   customDomain={campaignData.customDomain}
                                   campaign={campaignData}
                                   onPageBuilt={handlePageBuilt}
                                   onAngleStatusChange={handleAngleStatusChange}
                                   onChangeTrafficSource={async (src) => {
                                      setSelectedTrafficSource(src);
                                      setCampaignData(prev => ({ ...prev, trafficSource: src }));
                                      if (campaignData?.id) {
                                        try {
                                          const campaignRef = doc(db, 'campaigns', campaignData.id);
                                          await updateDoc(campaignRef, {
                                            trafficSource: src,
                                            updatedAt: new Date().toISOString()
                                          });
                                          console.log("💾 Persisted trafficSource change to Firestore.");
                                        } catch (err) {
                                          console.error("Error updating traffic source in Firestore:", err);
                                        }
                                      }
                                    }}
                                   onOpenAdStudio={(source) => {
                                     let platform = 'meta';
                                     if (source) {
                                       const srcLower = source.toLowerCase();
                                       if (srcLower.includes('google')) platform = 'google_search';
                                       else if (srcLower.includes('youtube')) platform = 'youtube';
                                       else if (srcLower.includes('taboola') || srcLower.includes('outbrain') || srcLower.includes('revcontent') || srcLower.includes('native')) platform = 'native';
                                     }
                                     setAdStudioPlatform(platform);
                                     setActivePageId('ad_studio');
                                   }}
                                   initialLink={(campaignData.customDomain && campaignData.customDomain !== 'xpgo.link' && campaignData.customDomain !== 'affiliateflow.ai')
                                     ? `https://${campaignData.customDomain}/${campaignData.slug || ''}`
                                     : `https://affiliateflow.ai/u/${campaignData.username || 'user'}/${campaignData.slug || ''}`}
                                 />
                               ) : activePageId === 'ad_studio' ? (
                                 <AdStudioModal 
                                    isEmbedded={true} 
                                    campaignData={campaignData} 
                                    initialPlatform={adStudioPlatform} 
                                    onClose={() => setActivePageId('funnel_map')}
                                    showCloseButton={true}
                                  />
                               ) : activePageId.startsWith('capture') ? (
                                 <CapturePopupEditor 
                                     activePageId={activePageId}
                                     campaignData={campaignData}
                                     setCampaignData={setCampaignData}
                                     handleSaveArchitecture={handleSaveArchitecture}
                                     userProfile={userProfile}
                                     mailerXpLists={mailerXpLists}
                                     mailerXpSegments={mailerXpSegments}
                                 />
                               ) : (
                                  <iframe 
                                    srcDoc={(() => {
                                      const rawHtml = livePages[activePageId];
                                      if (!rawHtml) return '<html><body style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:sans-serif;color:#64748b;background:#f8fafc;"><div>Rendering neural variant...</div></body></html>';
                                      const clickBlockScript = `
                                        <script>
                                          document.addEventListener('click', function(e) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                          }, true);
                                          document.addEventListener('submit', function(e) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                          }, true);
                                        </script>
                                      `;
                                      const bodyClose = rawHtml.toLowerCase().lastIndexOf('</body>');
                                      if (bodyClose !== -1) {
                                        return rawHtml.substring(0, bodyClose) + clickBlockScript + rawHtml.substring(bodyClose);
                                      }
                                      return rawHtml + clickBlockScript;
                                    })()} 
                                    className="w-full h-full border-none" 
                                  />
                               )}
                           </div>
                       </div>
                     ) : (
                       <div className="absolute inset-0 flex items-center justify-center">
                           {generating || isScouting ? (
                               <div className="text-center w-full max-w-md px-8 animate-in fade-in duration-500">
                                   <div className="relative mx-auto w-24 h-24 mb-8">
                                       <div className="absolute inset-0 border-4 border-indigo-500/20 rounded-full"></div>
                                       <div className="absolute inset-0 border-4 border-indigo-500 rounded-full border-t-transparent animate-spin"></div>
                                       <div className="absolute inset-0 flex items-center justify-center">
                                           <Bot size={32} className="text-indigo-400 animate-pulse" />
                                       </div>
                                   </div>
                                   <h3 className="text-xl font-black text-white tracking-widest uppercase mb-2">
                                       {isScouting ? 'Analyzing Offer Intelligence' : 'Synthesizing Neural Funnel'}
                                   </h3>
                                   <p className="text-sm text-slate-400 mb-6">
                                       {isScouting ? 'Scraping landing page and evaluating conversion potential...' : 'Generating high-converting HTML, assembling blueprints, and structuring copy...'}
                                   </p>
                                   <div className="w-full bg-slate-900 rounded-full h-1.5 overflow-hidden border border-slate-800 relative">
                                       <div className="absolute top-0 bottom-0 left-0 bg-indigo-500 rounded-full w-full animate-pulse"></div>
                                   </div>
                               </div>
                           ) : (
                               <div className="text-center opacity-50">
                                   <Bot size={64} className="text-slate-700 mx-auto mb-6" />
                                   <h3 className="text-xl font-black text-white tracking-widest uppercase">Waiting for Instructions</h3>
                                   <p className="text-sm text-slate-500 mt-2 max-w-sm mx-auto">Paste an affiliate link in the chat to automatically generate the optimal funnel sequence.</p>
                                </div>
                            )}
                        </div>
                    )}
                    </div>
                  </div>
                ) : null
              }
            </main>
         </div>



        {currentView !== 'agent-workspace' && isChatBubbleOpen && (
          <div className="fixed inset-y-0 right-0 w-[450px] bg-slate-900/95 backdrop-blur-3xl border-l border-slate-700/50 shadow-[-30px_0_80px_rgba(0,0,0,0.6)] flex flex-col z-[200] animate-in slide-in-from-right duration-300">
            <div className="h-24 border-b border-slate-800 flex items-center justify-between px-8 shrink-0 bg-indigo-600/5">
              <div className="flex items-center">
                <div className="size-12 bg-indigo-600 rounded-2xl flex items-center justify-center mr-4 shadow-lg shadow-indigo-600/20 relative">
                  <Bot size={24} className="text-white" />
                  <div className="absolute -top-1 -right-1 size-3.5 bg-emerald-400 border-2 border-slate-900 rounded-full z-10 animate-pulse"></div>
                </div>
                <div>
                  <h3 className="text-base font-black text-white uppercase tracking-widest">Ninja AI Agent</h3>
                  <div className="flex items-center text-[10px] text-indigo-400 font-bold uppercase tracking-widest mt-1">
                    Context Sync: <span className="text-emerald-400 ml-1">Live</span>
                  </div>
                </div>
              </div>
              <button onClick={() => setIsChatBubbleOpen(false)} className="p-2.5 text-slate-500 hover:text-white hover:bg-slate-800 rounded-xl transition-all">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-6 custom-scrollbar bg-slate-950/50">
              {builderHistory.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role !== 'user' && (
                    <div className="size-8 bg-indigo-900 border border-indigo-500/30 rounded-full flex items-center justify-center mr-3 shrink-0 mt-1 shadow-sm">
                      <Bot size={14} className="text-indigo-400" />
                    </div>
                  )}
                  <div className={`max-w-[85%] px-5 py-4 text-sm rounded-2xl leading-relaxed shadow-sm ${msg.role === 'user' ? 'bg-indigo-600 text-white rounded-tr-none' : 'bg-slate-800 text-slate-200 border border-slate-700/50 rounded-tl-none'}`}>
                    {msg.content}
                  </div>
                </div>
              ))}
              {isChatting && (
                <div className="flex justify-start">
                  <div className="size-8 bg-indigo-900 border border-indigo-500/30 rounded-full flex items-center justify-center mr-3 shrink-0 mt-1 shadow-sm">
                    <Bot size={14} className="text-indigo-400" />
                  </div>
                  <div className="bg-slate-800 px-5 py-4 rounded-2xl rounded-tl-none flex space-x-1.5 border border-slate-700/50 items-center">
                    <div className="size-1.5 bg-indigo-400 rounded-full animate-bounce"></div>
                    <div className="size-1.5 bg-indigo-400 rounded-full animate-bounce delay-75"></div>
                    <div className="size-1.5 bg-indigo-400 rounded-full animate-bounce delay-150"></div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-8 border-t border-slate-800 bg-slate-900">
              <div className="relative">
                <textarea 
                  value={chatPrompt} 
                  onChange={e => setChatPrompt(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleBuilderChat())}
                  placeholder="Hey Coach, update the funnel to..."
                  className="w-full bg-slate-950 border border-slate-800 rounded-2xl pl-5 pr-14 py-4 text-sm text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 h-28 resize-none shadow-inner custom-scrollbar"
                />
                <button 
                  onClick={handleBuilderChat} 
                  className="absolute bottom-4 right-4 p-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-500 transition-all shadow-lg active:scale-95"
                >
                  <ArrowRight size={20}/>
                </button>
              </div>
              <div className="flex items-center justify-between mt-4">
                  <p className="text-[10px] text-slate-500 uppercase font-black tracking-widest flex items-center">
                    <Zap size={10} className="mr-1.5 text-amber-400" /> Agentic Execution Enabled
                  </p>
                  <p className="text-[10px] text-slate-600 font-bold">Shift+Enter for newline</p>
              </div>
            </div>
          </div>
        )}

        {currentView !== 'agent-workspace' && !isChatBubbleOpen && (
          <div className="fixed bottom-8 right-8 z-[200]">
            <button 
              onClick={() => setIsChatBubbleOpen(true)}
              className="group relative p-1.5 bg-gradient-to-tr from-indigo-600 to-purple-600 rounded-full shadow-[0_0_40px_rgba(79,70,229,0.4)] hover:shadow-[0_0_60px_rgba(79,70,229,0.6)] transition-all transform hover:scale-110 active:scale-95 animate-in fade-in zoom-in"
            >
              <div className="absolute -top-1 -right-1 size-4 bg-emerald-400 border-2 border-slate-950 rounded-full z-10 animate-pulse"></div>
              <div className="bg-slate-950 p-4 rounded-full">
                <Bot size={32} className="text-white group-hover:scale-110 transition-transform" />
              </div>
              <div className="absolute right-full mr-4 top-1/2 -translate-y-1/2 whitespace-nowrap bg-indigo-600 text-white px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-xl">
                Ninja Agent Online
              </div>
            </button>
          </div>
        )}
      </div>
    )}
  </>
);
}

const CapturePopupEditor = ({ activePageId, campaignData, setCampaignData, handleSaveArchitecture, userProfile, mailerXpLists, mailerXpSegments }) => {
  const captureNode = campaignData.funnelArchitecture?.nodes?.find(n => n.id === activePageId);
  const initialConfig = captureNode?.data?.config || {
    headline: 'Get Instant Access',
    subtext: 'Enter your email to continue',
    btnText: 'Yes, Give Me Access →',
    listId: '',
  };

  const [headline, setHeadline] = useState(initialConfig.headline || 'Get Instant Access');
  const [subtext, setSubtext] = useState(initialConfig.subtext || 'Enter your email to continue');
  const [btnText, setBtnText] = useState(initialConfig.btnText || 'Yes, Give Me Access →');
  const [listId, setListId] = useState(initialConfig.listId || '');

  const [captureMethod, setCaptureMethod] = useState(campaignData.captureMethod || 'mailerxp');
  const [mailerXpListId, setMailerXpListId] = useState(campaignData.mailerXpListId || '');
  const [mailerXpSegmentId, setMailerXpSegmentId] = useState(campaignData.mailerXpSegmentId || '');
  const [customHtmlForm, setCustomHtmlForm] = useState(campaignData.customHtmlForm || '');

  const [saving, setSaving] = useState(false);

  // Sync state if activePageId or initialConfig changes
  useEffect(() => {
    if (captureNode) {
      const cfg = captureNode.data?.config || {};
      setHeadline(cfg.headline || 'Get Instant Access');
      setSubtext(cfg.subtext || 'Enter your email to continue');
      setBtnText(cfg.btnText || 'Yes, Give Me Access →');
      setListId(cfg.listId || '');
    }
  }, [activePageId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatedNodes = (campaignData.funnelArchitecture?.nodes || []).map(n => {
        if (n.id === activePageId) {
          return {
            ...n,
            data: {
              ...n.data,
              config: {
                headline,
                subtext,
                btnText,
                listId,
              }
            }
          };
        }
        return n;
      });

      const updatedCampaign = {
        ...campaignData,
        funnelArchitecture: {
          ...campaignData.funnelArchitecture,
          nodes: updatedNodes
        },
        captureMethod,
        mailerXpListId: captureMethod === 'mailerxp' ? mailerXpListId : '',
        mailerXpSegmentId: captureMethod === 'mailerxp' ? mailerXpSegmentId : '',
        customHtmlForm: captureMethod === 'custom' ? customHtmlForm : ''
      };

      setCampaignData(updatedCampaign);

      if (handleSaveArchitecture) {
        await handleSaveArchitecture(updatedNodes, campaignData.funnelArchitecture?.edges || []);
      }
      alert('✓ Capture popup settings successfully saved and applied to funnel!');
    } catch (err) {
      console.error(err);
      alert('Failed to save settings.');
    }
    setSaving(false);
  };

  return (
    <div className="flex flex-col md:flex-row h-full bg-[#050507] text-white overflow-hidden rounded-3xl">
      {/* Left Panel: Properties (Sleek Glassmorphic Form) */}
      <div className="w-full md:w-[420px] bg-slate-900 border-r border-slate-800 p-6 flex flex-col justify-between overflow-y-auto custom-scrollbar shrink-0">
        <div className="space-y-6">
          <div className="flex items-center space-x-3 pb-4 border-b border-slate-800">
            <div className="w-10 h-10 bg-[#02B6FF]/10 rounded-2xl flex items-center justify-center text-xl text-[#33D5E6] shadow-[0_0_10px_rgba(2,182,255,0.1)]">📧</div>
            <div>
              <h3 className="font-black text-sm uppercase tracking-widest text-white">Capture Popup Settings</h3>
              <p className="text-[10px] text-[#33D5E6] font-bold uppercase tracking-widest mt-0.5">Opt-in Overlay Config</p>
            </div>
          </div>

          {/* Headline */}
          <div className="space-y-2">
            <label className="text-[10px] text-slate-400 font-black uppercase tracking-widest block">Headline</label>
            <input 
              value={headline} 
              onChange={e => setHeadline(e.target.value)} 
              className="w-full bg-[#030306] border border-[#3b82f6]/10 text-white text-xs px-4 py-3 rounded-xl focus:border-[#3b82f6] outline-none transition-all focus:ring-1 focus:ring-[#3b82f6]/10"
              placeholder="Get Instant Access"
            />
          </div>

          {/* Subtext */}
          <div className="space-y-2">
            <label className="text-[10px] text-slate-400 font-black uppercase tracking-widest block">Subtext</label>
            <textarea 
              value={subtext} 
              onChange={e => setSubtext(e.target.value)} 
              className="w-full bg-[#030306] border border-[#3b82f6]/10 text-white text-xs px-4 py-3 rounded-xl focus:border-[#3b82f6] outline-none transition-all h-20 resize-none custom-scrollbar focus:ring-1 focus:ring-[#3b82f6]/10"
              placeholder="Enter your best email address to continue..."
            />
          </div>

          {/* Button Text */}
          <div className="space-y-2">
            <label className="text-[10px] text-slate-400 font-black uppercase tracking-widest block">Button Text</label>
            <input 
              value={btnText} 
              onChange={e => setBtnText(e.target.value)} 
              className="w-full bg-[#030306] border border-[#3b82f6]/10 text-white text-xs px-4 py-3 rounded-xl focus:border-[#3b82f6] outline-none transition-all focus:ring-1 focus:ring-[#3b82f6]/10"
              placeholder="Yes, Give Me Access →"
            />
          </div>

          <div className="h-px bg-slate-800"></div>

          {/* Autoresponder Settings */}
          <div className="space-y-4">
            <label className="text-[10px] text-slate-400 font-black uppercase tracking-widest block">Autoresponder Integration</label>
            
            <div className="flex space-x-4 mb-2">
              <label className="flex items-center space-x-2 text-xs text-slate-300 cursor-pointer">
                <input 
                  type="radio" 
                  name="editorCaptureMethod" 
                  value="mailerxp" 
                  checked={captureMethod === 'mailerxp'} 
                  onChange={() => setCaptureMethod('mailerxp')} 
                  className="text-[#02B6FF] focus:ring-[#02B6FF] bg-[#030306] border-slate-700" 
                />
                <span>MailerXP Native API</span>
              </label>
              <label className="flex items-center space-x-2 text-xs text-slate-300 cursor-pointer">
                <input 
                  type="radio" 
                  name="editorCaptureMethod" 
                  value="custom" 
                  checked={captureMethod === 'custom'} 
                  onChange={() => setCaptureMethod('custom')} 
                  className="text-[#02B6FF] focus:ring-[#02B6FF] bg-[#030306] border-slate-700" 
                />
                <span>Custom HTML Form</span>
              </label>
            </div>

            {captureMethod === 'mailerxp' && (
              <div className="space-y-4 animate-in fade-in duration-200">
                {userProfile?.mailerXpApi ? (
                  <div className="space-y-2">
                    <label className="block text-[10px] text-slate-400 font-black uppercase tracking-widest">MailerXP List ID</label>
                    <input 
                      value={mailerXpListId} 
                      onChange={e => setMailerXpListId(e.target.value)}
                      className="w-full bg-[#030306] border border-[#02B6FF]/10 text-white text-xs px-4 py-3 rounded-xl focus:border-[#02B6FF] outline-none transition-all focus:ring-1 focus:ring-[#02B6FF]/10"
                      placeholder="e.g. list_123abc"
                    />
                    <p className="text-[9px] text-slate-500 italic">Enter the MailerXP list identifier to sync opt-ins natively.</p>
                  </div>
                ) : (
                  <div className="text-[10px] text-slate-500 bg-[#030306] p-4 rounded-2xl border border-slate-850 italic leading-relaxed">
                    MailerXP API Key is not configured. Go to Global Settings to connect MailerXP, or choose <strong>Custom HTML Form</strong> to paste your raw autoresponder embed code.
                  </div>
                )}
              </div>
            )}

            {captureMethod === 'custom' && (
              <div className="space-y-2 animate-in fade-in duration-200">
                <label className="text-[8px] text-slate-500 font-black uppercase tracking-widest block">Paste Raw HTML Form Code</label>
                <textarea 
                  value={customHtmlForm} 
                  onChange={e => setCustomHtmlForm(e.target.value)} 
                  placeholder="<form action='https://...' method='post'>\n  <input type='email' name='email' />\n  ...\n</form>"
                  className="w-full bg-[#030306] border border-[#02B6FF]/10 text-white px-4 py-3 rounded-xl focus:border-[#02B6FF] outline-none text-xs font-mono h-32 custom-scrollbar focus:ring-1 focus:ring-[#02B6FF]/10"
                />
                <p className="text-[9px] text-slate-500 italic">Works with AWeber, GetResponse, Mailchimp, ActiveCampaign, etc.</p>
              </div>
            )}
          </div>
        </div>

        <button 
          onClick={handleSave} 
          disabled={saving} 
          className="mt-8 w-full py-4 bg-[#02B6FF] hover:bg-[#0A22EE] text-white text-xs font-black uppercase tracking-widest rounded-xl shadow-lg shadow-[#02B6FF]/10 active:scale-98 transition-all flex items-center justify-center font-mono"
        >
          {saving ? 'Saving...' : '✓ Save & Apply Changes'}
        </button>
      </div>

      {/* Right Panel: Interactive Live Mockup */}
      <div className="flex-1 bg-[#050507] flex items-center justify-center p-8 overflow-y-auto custom-scrollbar relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[#02B6FF]/5 via-[#050507] to-black pointer-events-none"></div>
        
        {/* Modal Mockup Container */}
        <div className="w-full max-w-lg bg-slate-900 border border-slate-800 text-white p-8 md:p-10 rounded-[2.5rem] shadow-[0_0_80px_rgba(2,182,255,0.06)] flex flex-col items-center text-center relative z-10 animate-in fade-in scale-95 duration-300 hover:border-[#02B6FF]/20 transition-all">
          <div className="absolute top-5 right-5 text-slate-500 p-2 rounded-full hover:bg-slate-800 cursor-not-allowed">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </div>
          <div className="w-20 h-20 bg-gradient-to-tr from-[#33D5E6] via-[#02B6FF] to-[#0A22EE] rounded-3xl flex items-center justify-center shadow-lg shadow-[#02B6FF]/20 mb-6 animate-pulse">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
          </div>
          <h3 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#33D5E6] to-[#02B6FF] tracking-tight leading-tight mb-4 drop-shadow-[0_0_15px_rgba(2,182,255,0.1)]">
            {headline || 'Unlock Instant Access'}
          </h3>
          <p className="text-slate-400 text-sm md:text-base mb-8 leading-relaxed max-w-sm">
            {subtext || 'Enter your best email address below to continue.'}
          </p>
          <div className="w-full space-y-4">
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-slate-500">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.206"></path></svg>
              </span>
              <input type="email" disabled placeholder="Enter your best email address..." className="w-full pl-12 pr-4 py-4 bg-[#030306] border border-slate-800 rounded-2xl text-white placeholder-slate-500 text-sm cursor-not-allowed" />
            </div>
            <button className="w-full bg-gradient-to-r from-[#33D5E6] via-[#02B6FF] to-[#0A22EE] hover:from-[#33D5E6] hover:to-[#02B6FF] text-white font-extrabold text-sm py-4 rounded-2xl shadow-lg shadow-[#02B6FF]/25 flex items-center justify-center gap-3 uppercase tracking-wider font-mono">
              <span>{btnText || 'Yes, Give Me Access →'}</span>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-6 flex items-center justify-center gap-2">
            <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>
            We respect your privacy. Spam-free experience.
          </p>
        </div>
      </div>
    </div>
  );
};

export default App;
